// App-shell nav, derived from the PowerBuilder top-menu structure.
//
// The PB menu hierarchy lives in `nav.generated.json` — it's projected from
// `docs/pb-menu-tree.json` (a parse of `m_main.srm`) by `scripts/build-nav.mjs`. We re-render
// PB's full m_file / m_activities / m_inquiry / m_managewarehouse / m_reports / m_configuration
// / m_administrator / m_interfaces / m_help structure in the sidebar so users keep their
// muscle memory; only items whose PB window is in {@link WINDOW_TO_ROUTE} render as live
// links, the rest gray out.
//
// Five top-level menus PB has are dropped (Edit / Sheet / Window / Options / Favorites) — see
// `scripts/build-nav.mjs` for why. Our own favorites + collapsed-rail surface lives in
// {@link Sidebar}.

import navData from './nav.generated.json';
import routeWindowsData from './generated/route-windows.json';
import windowPoliciesData from './generated/window-policies.json';
import {
  createScreenAccess,
  hasLoadedGrants,
  windowGateModeOf,
  type ScreenAccess,
  type WindowCatalog,
} from '@/shared/auth/screenAccess';
import type { AuthSession } from '@/shared/auth/types';
import {
  FOCUS_NAV_SEARCH_EVENT,
  OPEN_SCREEN_HELP_EVENT,
  type AppShortcutEvent,
} from '@/shared/ui/appShortcuts';

export type NavTreeItem = {
  /** PB menu id, e.g. m_orderprocessing. Used as the React key + collapse-state key. */
  id: string;
  /** Visible caption, ampersand-stripped. {@code null} only on separators. */
  label: string | null;
  isSeparator?: boolean;
  /** PB window the {@code clicked} event opened; correlated to {@link WINDOW_TO_ROUTE}. */
  window?: string;
  children?: NavTreeItem[];
};

export type NavTreeGroup = NavTreeItem & { label: string; children: NavTreeItem[] };

/**
 * PB menu items PH10 will NOT port (John, 2026-07-08): pruned from the nav entirely —
 * not grayed out — because "coming soon" would be a lie. Keep the ROUTE alive where one
 * exists (old tester-issue links must not 404). Decision notes:
 * docs/design/user-defined-reports-wont-port.md.
 */
const WONT_PORT_NAV_IDS = new Set(['m_userdefinedreports_ph', 'm_userdefinedreports']);

/**
 * ADR-0028 (hidden-in-PB ⇒ hidden-in-PH10), applied by MENU ID — slice-3454, from the
 * 2026-08-25 nav-graybar review (rows N3/N9/N10/N12/N13/N28-N48/N53/N56/N63-N79).
 *
 * <p>Why an ID set and not the {@link RouteEntry.hiddenInPb} flag: that flag hangs off a
 * ROUTE, so {@code isItemVisible()} could only reach it through {@code resolveRoute()}. Every
 * item here has NO route (PB's caption and clicked event live in the m_phfc_main ancestor, so
 * the menu parse recorded no window), which meant the hidden-in-PB filter was bypassed
 * entirely and the item rendered as a dead dimmed row. The flag also cannot express a hidden
 * BRANCH — six of the ids below are branches (Translation, Security, System Phfc, its Field
 * Format sub-branch, Auto Process, Ad Hoc) whose children are hidden only because the parent
 * is, and 27 of the 45 rows come off the tree that way.
 *
 * <p>Every id is verified against the PB source: {@code docs/pb-menu-ancestor.json} carries
 * the {@code visible} flag extracted from BOTH m_main.srm and m_phfc_main.srm, and
 * {@code navPbHiddenParity.test.ts} fails if any id here is not PB-hidden on itself or on an
 * ancestor branch. Routes (where one exists) stay registered — this prunes the MENU, not code.
 */
export const HIDDEN_IN_PB_NAV_IDS: ReadonlySet<string> = new Set([
  // ── Activities ────────────────────────────────────────────────────
  'm_carouselprocess', // N3 Pick to Light — m_carouselprocess.create visible=false; both children SME-dropped (Russ)
  'm_activitycalendar', // N9 — visible=false AND enabled=false; delegates to the hidden PHFC twin
  'm_phfc_activitycalendar', // N10 — the PHFC twin, also visible=false + enabled=false
  // ── Reports ───────────────────────────────────────────────────────
  'm_billingtransactionsummarybydate', // N12 — visible=false; no w_rep_bill_trans.srw in the export either
  'm_misc', // N13 Reports > Misc — visible=false, no clicked script anywhere (vestigial)
  // ── Configuration > Translation (branch, N28-N32) ─────────────────
  'm_translation_phfc', // m_main sets the branch visible=false; live label rewriting is crt_translate_sum, not this screen
  'm_languages_phfc',
  'm_windowtranslation_phfc',
  'm_messagetranslation_phfc',
  'm_tablecolumntranslation_phfc',
  'm_menutranslation_phfc',
  // ── Configuration > Security (branch, N33-N36) ────────────────────
  'm_security_phfc', // branch visible=false; the real Security screen is /config/security (CONFIGURATION-SECURITY)
  'm_users_phfc',
  'm_groups_phfc',
  'm_modules_phfc',
  'm_windowmodules_phfc',
  // ── Configuration > System Phfc (branch, N37-N45) ─────────────────
  'm_system_phfc', // branch visible=false AND enabled=false — framework twin of the Configuration > System leaves PB does show
  'm_configuration_phfc',
  'm_windowtoolbarmanagement_phfc',
  'm_workstations_phfc',
  'm_windowdefaultset_phfc',
  'm_enabledisablerequired_phfc',
  'm_fieldformat_phfc',
  'm_fieldformatrules_phfc',
  'm_fieldformats_phfc',
  'm_windowfieldformats_phfc',
  'm_windowfieldmask_phfc',
  // NOTE: m_wizards_phfc also lives under this branch. It keeps its devDisabled route entry
  // (Bob 2026-07-16) rather than moving here — the reason differs and the flag is the record.
  // ── Administrator ─────────────────────────────────────────────────
  'm_movermaintenance', // N46 — m_main overrides the inherited item visible=false (dbxx.mover is still read elsewhere)
  'm_autoprocess_phfc', // N47/N48 branch — m_autoprocess_phfc.create visible=false
  'm_autoprocessservermaintenance',
  'm_autoprocesstimer',
  // ── Interfaces ────────────────────────────────────────────────────
  'm_events', // N53 — m_events.create visible=false; children live on a separate EventDatabase connection (dropped)
  // ── Help ──────────────────────────────────────────────────────────
  'm_search', // N56 — visible=false, and the action opens a Windows .hlp file (dead technology)
  // Help > QSSI Tools (N63-N76). The BRANCH is visible=false in m_phfc_main, but PH10 has
  // three LIVE ported screens hanging off it (Label View, Label Mapping, Label Generation)
  // and for two of them this is the only sidebar path — so the branch itself STAYS and its
  // hidden leaves are listed individually. See the report for Chris; pruning the branch
  // outright is a one-line change if he prefers PB parity over reachability.
  'm_extractwindowmodule',
  'm_test',
  'm_allowtosetfieldrequired',
  'm_datawidnowoptions',
  'm_queryanalyzer',
  'm_opensystemtempfolder',
  'm_currentwindowinfo',
  'm_allowtosetwindowdefault', // own visible=false too
  'm_haltsystem', // `halt` — kills the app; must never be exposed
  'm_adhoc', // sub-branch carrying N72/N73
  'm_adhocrptreview',
  'm_adhocrptmaint',
  'm_base64stringconvetor',
  'm_tableoptions',
  'm_qssitool_dash1', // inherited `text = "-"` separator inside the hidden branch
  // N77-N79: invisible accelerator carriers. They exist only to own F10/F11/F12 and forward
  // to the focused sheet's ue_fkeyNN handler — never menu entries. PH10 keyboard shortcuts
  // belong in the keyboard layer, not the nav tree.
  'm_fkey10',
  'm_fkey11',
  'm_fkey12',
]);

/**
 * PB SHOWS these, but PH10 triaged them out — slice-3454, nav-graybar review rows
 * N1/N2/N14/N22/N27/N49-N52/N54/N59. Same prune, different reason: not PB parity but a
 * documented scope decision, so the two lists stay separate (a future PB-parity audit must
 * not mistake these for hidden-in-PB items). Routes stay registered so old tester links live.
 */
export const TRIAGED_OUT_NAV_IDS: ReadonlySet<string> = new Set([
  // N1 Quick Pick Confirmation (w_quickpick) — RESTORED slice-4913 (Russ / John, 2 Nov 2026):
  // the 24 May triage drop is reversed; the window is built at /orders/quick-pick.
  // N2 Order Allocation (w_order_allocation) — DROPPED (slice-251 follow-up): BRITANNICA-only
  // Great Plains SOP integration (fsp_gp_sop_allocate/_validate) needing a per-tenant GP DB
  // connection; the core delete path is already LIVE via PurgeService.
  'm_orderallocation',
  // N14 Run Custom Report (w_genrpt_custom_rep) + N27 Create Custom Report (w_generic_rep) —
  // the custom-report DESIGNER family is explicitly out of scope (docs/reports-backlog.md
  // header, docs/triage-2026-05-24.md). PH10's report architecture is service-driven
  // (JasperRenderer + AdHocTableRenderer); there is no DataWindow editor to point them at.
  'm_runcustomreport',
  'm_createcustomreport',
  // N22 Window Default Set — not a window at all: m_windowdefaultset_phfc calls GetFocus(),
  // demands the focused control be a DataWindow and saves that DW's layout as the default
  // ("The focus object is not a Datawindow" otherwise). PB DataWindow meta-tooling with no web
  // analogue — PH10 layouts live in code + the saved-view profile bars. (Chris, 2026-08-25.)
  'm_windowdefaultset',
  // N49-N51 the three w_if_maint modes — DROPPED (slice-251): a DBA-facing utility that walks
  // sys.syscolumns for interface-table column metadata. No operator-facing semantic; DBAs use
  // SSMS. If reversed it is one window + three modes = three routes (dev-rules §4.23).
  'm_downloadinterfacemaintenance',
  'm_generaluploadinterfacemaintenance',
  'm_triggertablemaintenance',
  // N52 Host Interface Program — shells out to a local EXE named by the Client/IFProgram
  // workstation setting; no web analogue. Already recommended in
  // docs/audits/client-mode-gated-fields.md #2 + fix-list #3: w_frame.srw:234-239 hides it for
  // every tenant whose mode is not FURSYS, and FURSYS is not even a declared mode constant.
  // (That audit also named `interface/interfacebase` — the Base System BRANCH — which is NOT
  // pruned here: PH10 has 14 live ported screens under it that testers use today.)
  'm_hostinterfaceprogram',
  // N54 Pitney Bowes — integration retired (Bob, SME survey); both children (w_pb_ship_dwn,
  // w_pb_ship_upl) are in build-nav.mjs's DROPPED_WINDOWS, so the branch renders empty. An
  // empty parent is worse than no parent.
  'm_pitneybowes',
  // N59 PB Color Selector — a PowerBuilder colour-constant picker for developers. No meaning
  // in a web app. (Its sibling Serial Port Test is ALIASED to /hardware-test — see N60 in
  // ID_TO_ROUTE below — so Help > Tools keeps exactly one, working, leaf.)
  'm_pbcolorselector',
  // ── slice-3471: the two PB DataWindow meta-tools Chris pruned on 2026-08-25 ──────────
  // N23 Enable/Disable Required — not a window either: m_enabledisablerequired's clicked
  // event calls GetFocus(), insists the focused control is a DataWindow, then flips the
  // required flag for that window/column pair in dbxx.datawin_option (473 rows, gated by
  // the allow_to_set_req_flag setting). PH10 references datawin_option NOWHERE —
  // required-ness is declared in the browse/page configs in code — so a maintenance grid
  // over it would be a control that silently does nothing. Same shape as N22 Window
  // Default Set above. If per-customer required-ness ever becomes a real requirement it is
  // a platform slice (honour datawin_option in the config layer) with its own ADR, not a
  // menu leaf. (Chris, 2026-08-25, ph10-nav-decisions.html N23.)
  'm_enabledisablerequired',
  // N26 Window Field Formats — w_phfc_window_field_formats over dbxx.field_formats_for_window
  // (7 rows): the mapping that binds "window X column Y uses field-format Z". Unlike its two
  // siblings (N24/N25, both BUILT and wired below) this one was never built, and it is the
  // only one of the three that is useless without the runtime side — it exists purely to
  // attach a mask to a live PB DataWindow column, and PH10 does not apply input masks at
  // runtime. Pruned until it does; revisit together with the runtime-mask slice.
  // (Chris, 2026-08-25, ph10-nav-decisions.html N26.) NOTE the id: the menu parser suffixes
  // the m_main leaf `m_windowfieldformats0` to disambiguate it from the hidden PHFC twin
  // `m_windowfieldformats_phfc` (already in HIDDEN_IN_PB_NAV_IDS above).
  'm_windowfieldformats0',
]);

/** Menu ids PH10 does not render at all, whatever the reason. */
function isPrunedNavId(id: string | undefined): boolean {
  if (!id) return false;
  return (
    WONT_PORT_NAV_IDS.has(id) ||
    HIDDEN_IN_PB_NAV_IDS.has(id) ||
    TRIAGED_OUT_NAV_IDS.has(id)
  );
}

/** Drop pruned ids — and, when the id is a branch, its whole subtree with it. */
function prunePbMenu(items: NavTreeItem[]): NavTreeItem[] {
  return items
    .filter((i) => !isPrunedNavId(i.id))
    .map((i) => (i.children ? { ...i, children: prunePbMenu(i.children) } : i));
}

/** slice-3886 (ADR-0064): Reports > Report Builder. A PH10-native screen with no PB window, so it
 *  rides the tree machinery as a synthetic window (see WINDOW_TO_ROUTE) placed right after the
 *  AI report assistant leaf. Always present (not demo-only); the route is REPORTS-gated. */
export const REPORT_BUILDER_ITEM: NavTreeItem = {
  id: 'ph10_report_builder',
  label: 'Report Builder',
  window: 'ph10_report_builder',
};

function withReportBuilder(groups: NavTreeGroup[]): NavTreeGroup[] {
  return groups.map((g) => {
    if (g.id !== 'm_reports') return g;
    const at = g.children.findIndex((c) => c.window === 'w_ai_report_assistant');
    const children = [...g.children];
    children.splice(at >= 0 ? at + 1 : 0, 0, REPORT_BUILDER_ITEM);
    return { ...g, children };
  });
}

/**
 * slice-4210 (Chris 2026-10-14): Print Log (slice-223, PH10's audit trail of every print
 * request — no PB window) lives in the PB menu under Configuration › Labels/Reports ›
 * Printers, after the four PB printer-config windows, and carries their module
 * (CONFIGURATION-LABELS/REPORTS on window_modules_act for w_label_printers /
 * w_report_printers / w_location_printers / w_printer_type). The pseudo-window id
 * {@code ph10_print_log} resolves through {@link WINDOW_TO_ROUTE} like the slingshot_* leaves.
 */
export const PH10_PRINT_LOG_ITEM: NavTreeItem = {
  id: 'ph10_print_log',
  label: 'Print Log',
  isSeparator: false,
  window: 'ph10_print_log',
};

function withPh10PrintLog(items: NavTreeItem[]): NavTreeItem[] {
  return items.map((it) => {
    if (it.id === 'm_printers_cfg') {
      return { ...it, children: [...(it.children ?? []), PH10_PRINT_LOG_ITEM] };
    }
    return it.children ? { ...it, children: withPh10PrintLog(it.children) } : it;
  });
}

export const NAV_TREE: NavTreeGroup[] = withPh10PrintLog(
  withReportBuilder(
    prunePbMenu((navData as { groups: NavTreeGroup[] }).groups) as NavTreeGroup[],
  ),
) as NavTreeGroup[];

/** Live PB-window → PH10-route mapping. Items not in this map render as grayed-out
 *  placeholders so the customer sees the full PB menu shape (this is intentional — see
 *  `docs/dev-rules.md` once §4.9 lands). Add an entry the moment you wire a new screen. */
export type RouteEntry = {
  to: string;
  requiredModule?: string;
  requiredLevel?: number;
  /** True when this entry maps to the generic `/pb/:window` placeholder page. Sidebar
   *  renders these dimmer / italic so the customer can tell which screens are real vs
   *  "coming soon — same place in the menu as PB". Generated by
   *  `scripts/build-placeholder-routes.mjs`. */
  placeholder?: boolean;
  /** PB parity (Chris 2026-07-13): PB's m_main creates this window's menu item
   *  `visible = false` and NOTHING ever un-hides it — the screen is unreachable in the
   *  running PB app. We mirror that: isItemVisible() drops the item from the sidebar and
   *  generate-page-catalog.mjs skips it (no tester sign-off row). The ROUTE itself stays
   *  registered (direct URL still works) so no code is deleted. Audit source: m_main.srm
   *  visible=false sweep, docs/audits/signoff-wave2-2026-07-13/CAMPAIGN-STATE.md. */
  hiddenInPb?: boolean;
  /** Screen that exists in PB but never made it out of dev in PH10 and is being pulled
   *  from the running app for now (Bob's call, 2026-07-16). Behaves like {@link hiddenInPb}
   *  — isItemVisible() drops it from the sidebar and generate-page-catalog.mjs skips it (no
   *  tester sign-off row) — but is a SEPARATE flag because the REASON differs: PB is NOT
   *  hiding this (a PB-parity audit would find it visible), so it must not be conflated with
   *  hiddenInPb. The ROUTE stays registered so no code is deleted; flip this off to restore. */
  devDisabled?: boolean;
};

export const WINDOW_TO_ROUTE: Record<string, RouteEntry> = {
  // ── slice-3626 (ADR-0050): customer-DEMO-only screens ported from the Slingshot POC.
  // Synthetic "windows" (no PB analogue) so they ride the normal tree machinery; the items
  // themselves are injected by withDemoNav() only when the instance's demoSystem flag is on.
  slingshot_floor_pulse: { to: '/reports/floor-pulse' },
  slingshot_labor_by_user: { to: '/reports/labor-by-user' },
  slingshot_labor_uph: { to: '/reports/labor-uph' },
  slingshot_monthly_kpi: { to: '/reports/monthly-kpi' },
  slingshot_shift_recap: { to: '/reports/shift-recap' },
  slingshot_user_scorecard: { to: '/reports/user-scorecard' },
  slingshot_user_travel: { to: '/reports/user-travel' },
  slingshot_labor_task_assign: { to: '/reports/labor-task-assign' },
  // slice-3634 (docs/design/work-visualization.md): overhead map + work heatmap, demo-only.
  slingshot_work_visualization: { to: '/reports/work-visualization' },
  // slice-3639 (docs/design/warehouse-status.md): the one-page KPI / attention board. Chris
  // 2026-09-13: "make this live under Manage Warehouse — no submenu — call it 'Warehouse
  // Status'", so it is the only demo leaf that does NOT live under /reports.
  slingshot_warehouse_status: { to: '/manage-warehouse/warehouse-status' },
  // slice-4210: Print Log under Configuration › Labels/Reports › Printers (see PH10_PRINT_LOG_ITEM).
  ph10_print_log: { to: '/admin/print-log', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  slingshot_rf_simulator: { to: '/tools/rf-simulator' },
  // ── Hand-written single-record screens ────────────────────────────
  w_order_search: { to: '/orders/search', requiredModule: 'ACTIVITIES-OP-ORDERS-ADMIN' },
  // FB#121 (ROGER) / slice-376 + slice-388 fix: w_receipt_search used to be
  // mapped here to /receipts/maintenance (the Tier-2 single-record form)
  // -- that route belongs to w_receipt_maint, not w_receipt_search. The
  // correct w_receipt_search mapping (to the /browse/receipt-maint list cfg)
  // lives further down in this map. Old line removed to fix the duplicate-
  // key build error from slice-376.
  w_inv_forecast_by_item: { to: '/inventory/inquiry', requiredModule: 'INQUIRY-ITEM' },
  w_location_status: { to: '/location/status', requiredModule: 'INQUIRY-ITEM' },

  // ── Generic table-browser screens (slice-31) ──────────────────────
  // Each maps a PB w_*_search / w_*_status / config-list window to /browse/<key>; the
  // backing `key` is registered in TableBrowserRegistry.java + browseConfigs.ts. Adding
  // more is a single line here once both registry entries exist.
  w_cartlook: { to: '/cartons/lookup' },
  w_truck_status: { to: '/browse/truck-status' },
  // slice-829: PB w_truck_status_detail is the 7-tab inquiry opened by double-clicking a
  // truck row in w_truck_status. Route needs the truck key (warehouseId/truckId); base
  // path recorded for menu correlation, live drill-down built per-row in the truck-status
  // browse cfg (rowDrillDown).
  w_truck_status_detail: { to: '/shipping/truck-status-detail' },
  w_truck_search: { to: '/browse/trucks' },
  // slice-3259: opened from Truck Search's Import toolbar item (w_truck_search.srw:986-991), so
  // it has no menu leaf of its own — mapped here so tester reports naming the PB window resolve.
  w_truck_pop_import: { to: '/truck/import' },
  w_shipments: { to: '/browse/shipments' },
  w_hist_search: { to: '/browse/if-transactions' },
  w_change_log: { to: '/browse/mw-change-log' },
  w_picks: { to: '/browse/pick-status' },
  w_waves: { to: '/browse/wave-status' },
  // slice-830: PB w_wave_det is the 7-tab inquiry opened by double-clicking a wave row in
  // w_waves. Route needs the wave key (warehouseId/waveSeqNum); base path recorded for menu
  // correlation, live drill-down built per-row in the wave-status browse cfg (rowDrillDown).
  w_wave_det: { to: '/orders/wave-detail' },
  w_lost_found_search: { to: '/browse/cyc-lost-found' },
  w_labor_task_search: { to: '/labor-tasks' },
  w_item_class: { to: '/browse/cfg-item-classes' },
  w_inv_type: { to: '/config/inv-types' }, // slice-50: promoted /browse -> /config (matches PB single-record edit shape)
  w_location_type: { to: '/browse/cfg-location-types' },
  w_routes: { to: '/browse/cfg-routes', requiredModule: 'CONFIGURATION-ORDER' },
  // slice-824: PB w_route_detail is the 4-tab detail editor invoked from
  // w_routes (Default/By Zone/By States/By Zips). Each tab edits a different
  // route_detail_* table sharing (route_code, line) PK. /config/route-detail
  // hosts the TabbedRouteDetailPage; operators filter by route_code at the
  // top of each tab's criteria form.
  w_route_detail: { to: '/config/route-detail' },
  // slice-825: PB w_item_class_detail is the 5-tab detail editor invoked from
  // w_item_class. Edits one dbxx.item_class row across General / Groups and Rules /
  // UOM and Labels / Pick and Pack / Miscellaneous tabs.
  w_item_class_detail: { to: '/config/item-class-detail' },
  // slice-826: PB w_usr_grp_det is the 4-tab tag/untag assignment window
  // (Owners / Warehouses / Modules / Work Modes) invoked from w_usr_grp.
  w_usr_grp_det: { to: '/config/user-group-detail' },
  // slice-828: PB w_hi_orddata is the 5-tab "Shipped Product History for Order"
  // inquiry, opened from w_order_maint's Shipped Product toolbar action for the
  // focused order. The route needs the order key (ownerId/orderId/releaseNum);
  // the base path is recorded for menu correlation, the live drill-down link is
  // built per-row in OrderMaintenancePage.
  w_hi_orddata: { to: '/orders/shipped-history' },
  w_usrs: { to: '/browse/cfg-users' },
  w_vendor: { to: '/browse/cfg-vendors' },
  w_customer_master_search: { to: '/browse/cfg-customer-masters' },
  // ── slice-34 bulk-port batch ──
  w_trailer_search: { to: '/yard/trailer-search', requiredModule: 'ACTIVITIES-YARDMANAGE-MENU' },
  w_truckinbound_search: { to: '/browse/inbound-trucks', requiredModule: 'ACTIVITIES-RP-TRUCKS-MENU' },
  w_kit_run_search: { to: '/browse/kit-runs' },
  w_production_run_search: { to: '/browse/production-runs', requiredModule: 'ACTIVITIES-PRODUCTION-ADMIN' },
  w_action_log: { to: '/browse/action-log' },
  w_event_req_log: { to: '/browse/event-log' },
  w_usr_login_log: { to: '/browse/user-login-log' },
  w_orderstat: { to: '/browse/orders-status' },
  w_order_status_history: { to: '/browse/order-status-history' },
  w_pre_receipt_search: { to: '/browse/pre-receipts', requiredModule: 'ACTIVITIES-RECEIPT-ENTRY' },
  w_inbound_asn_search: { to: '/browse/inbound-asn' },
  w_invoice_search: { to: '/browse/invoices' },
  w_bill_summary_search: { to: '/browse/billing-summary' },
  w_bill_transaction_search: { to: '/browse/billing-transactions' },
  w_item_cost_search: { to: '/browse/item-costs', requiredModule: 'ACTIVITIES-COSTING-MENU' },

  // ── slice-35d bulk-port batch ──
  w_wave_load_plan_search: { to: '/browse/wave-load-plans', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' },
  // slice-816: PB w_production_status is ONE window with 6 read-only inquiry
  // tabs (Item / Item/Loc / Item/Detail / Lines / Runs / Locations). Previously
  // routed to /browse/production-status which only surfaced PB tab 4 (Lines).
  // /inquiry/production-status hosts all 6 in TabbedProductionStatusPage.
  w_production_status: { to: '/inquiry/production-status' },
  w_wf_process_search: { to: '/browse/workflow-process' },
  w_pa_request_bulk: { to: '/browse/putaway-bulk' },
  w_block_locations: { to: '/browse/block-locations', requiredModule: 'MANAGE-WAREHOUSE-REWAREHOUSING' },
  w_global_receipt_line_maint: { to: '/browse/receipt-lines', requiredModule: 'ACTIVITIES-RECEIPT-INQUIRY' },
  w_sample_request: { to: '/browse/sample-requests', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' },
  w_change_log_grp: { to: '/browse/change-log-groups' },
  w_change_log_type: { to: '/browse/change-log-types', requiredModule: 'CONFIGURATION-CHANGELOG' },
  w_alarm_type: { to: '/config/alarm-types', requiredModule: 'CONFIGURATION-ALARM' }, // slice-91: promoted /browse -> /config (Tier-2 form; alarm_category deferred)
  w_alarm_notification: { to: '/browse/alarm-notifications' },
  w_owner_list: { to: '/browse/cfg-owners' },
  // slice-832: PB w_owner EDIT mode is the 9-tab maintenance window opened from the
  // owner-list "Maintenance" action with owner_id. Base path recorded for menu
  // correlation; the live drill builds /owners/:ownerId per row.
  w_owner: { to: '/owners' },
  w_carrier_door: { to: '/browse/carrier-doors' },


  // ── slice-38 inquiry-menu screens ─────────────────────────────────────
  w_item_search: { to: '/browse/items-search', requiredModule: 'INQUIRY-ITEM' },
  w_item_status: { to: '/items/status', requiredModule: 'INQUIRY-ITEM' },
  w_adjust_search: { to: '/browse/adjust-search', requiredModule: 'INQUIRY-ITEM' },
  w_ship_misc_search: { to: '/browse/misc-shipping-search', requiredModule: 'INQUIRY-ITEM' },
  // slice-4498 (Q34, ADR-0001 §3): w_location_search -> INQUIRY-LOCATION (window_modules_act); was the INQUIRY-ITEM placeholder.
  w_location_search: { to: '/browse/cfg-locations', requiredModule: 'INQUIRY-LOCATION' },

  // ── slice-41 bulk-browse routes ─────────────────────────────────────

  w_receipt_status: { to: '/browse/receipt-status' },
  w_prod_delv_hist_search: { to: '/browse/prod-delv-hist-search' },
  w_order_tag_search: { to: '/browse/order-tag-search' },
  w_pick_ticket_assign_search: { to: '/browse/pick-ticket-assign-search' },
  w_cart_assign_search: { to: '/browse/cart-assign-search', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' },
  w_order_assign_search: { to: '/browse/order-assign-search' },
  w_label_print_history: { to: '/labels/reprint', requiredModule: 'ACTIVITIES-OP-CARTONS' },
  w_trashed_pallets_search: { to: '/browse/trashed-pallets-search' },
  w_order_status: { to: '/browse/order-status' },
  w_customer_shipto_search: { to: '/browse/customer-shipto-search' },
  w_warehouse_list: { to: '/browse/warehouse-list' },
  // slice-831: PB w_warehouse EDIT mode is the 12-tab maintenance window opened from the
  // warehouse-list "Maintenance" action with warehouse_id. Base path recorded for menu
  // correlation; the live drill builds /warehouses/:warehouseId per row.
  w_warehouse: { to: '/warehouses' },
  // slice-836: w_warehouse toolbar children. Counters opens per-warehouse ctr_misc2;
  // Min/Max + RF Business Defaults are browse grids. Live drills built per-row on the
  // warehouse-list cfg.
  w_warehouse_ctr: { to: '/warehouses' },
  w_item_whse: { to: '/browse/item-warehouse-minmax' },
  w_rf_business_defaults: { to: '/browse/rf-business-defaults' },
  w_cust_ddw_header_search: { to: '/browse/cust-ddw-header-search', requiredModule: 'CONFIGURATION-SYSTEM' },
  w_rf_profile_list: { to: '/browse/rf-profile-list', requiredModule: 'CONFIGURATION-SYSTEM' },
  w_receipt_shipto_search: { to: '/browse/receipt-shipto-search', requiredModule: 'CONFIGURATION-RECEIPT' },
  // G-057 / H-028: the Interfaces > Upload/Download Maintenance leaves sit under submenu
  // Interfaces.BaseSystem.IfMaintU (window_modules None_00300) -> INTERFACES-MAINTENANCE.
  w_if_ul_receipts_search: { to: '/browse/if-ul-receipts-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_ul_orders_search: { to: '/browse/if-ul-orders-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_download_ship_search: { to: '/browse/if-download-ship-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_carton_shipping_search: { to: '/browse/if-carton-shipping-search' }, // ADR-0025 port (slice-1593)
  w_if_ul_order_status_search: { to: '/browse/if-ul-order-status-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_ul_transaction_maintenance_search: { to: '/browse/if-ul-transaction-maintenance-search' },
  w_if_ul_ordshipment_search: { to: '/browse/if-ul-ordshipment-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_ul_truckship_search: { to: '/browse/if-ul-truckship-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_ul_ship_maintenance_search: { to: '/browse/if-ul-ship-maintenance-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_if_ul_item_search: { to: '/browse/if-ul-item-search', requiredModule: 'INTERFACES-MAINTENANCE' },
  w_interface_services_search: { to: '/browse/interface-services-search' },



  // ── slice-42 bulk-browse routes ─────────────────────────────────────

  w_vendor_price_quote_search: { to: '/browse/vendor-price-quote-search', requiredModule: 'ACTIVITIES-RECEIPT-INQUIRY' },
  w_truck_inout_search: { to: '/browse/truck-inout-search', requiredModule: 'ACTIVITIES-YARDMANAGE-MENU' },
  w_costing_by_shipment_search: { to: '/browse/costing-by-shipment-search', requiredModule: 'ACTIVITIES-COSTING-MENU' },
  w_work_search: { to: '/browse/work-search' },
  // Slice-427: PB w_inv_back_to_stock is a data-entry workflow window, not
  // a search/browse. New page at /inventory/back-to-stock takes the header
  // form + line accumulator + submit (legacy /browse/inv-back-to-stock route
  // removed -- the browse cfg was a misclassification).
  w_inv_back_to_stock: { to: '/inventory/back-to-stock' },
  w_order_fastpick: { to: '/browse/order-fastpick' },
  w_change_log_legacy: { to: '/browse/change-log-legacy', requiredModule: 'MANAGE-WAREHOUSE-LOGS' },
  w_event_requests: { to: '/browse/event-requests' },
  w_exception: { to: '/browse/exception' },
  w_action_log_type: { to: '/browse/action-log-type', requiredModule: 'CONFIGURATION-CHANGELOG' },
  w_event_type: { to: '/browse/event-type', requiredModule: 'CONFIGURATION-EVENT' },
  w_event_rep_email_cfg: { to: '/browse/event-rep-email-cfg', requiredModule: 'CONFIGURATION-EVENT' },
  w_bill_event_periodic: { to: '/browse/bill-event-periodic' },
  w_bill_event_activity: { to: '/browse/bill-event-activity' },
  // slice-820: PB w_alarms is ONE window with 2 tabs partitioning dbxx.alarm
  // by aggregate_flag (Y/N). Previously routed to /browse/alarms which showed
  // all rows mixed. /inquiry/alarms hosts both partitions in TabbedAlarmsPage.
  w_alarms: { to: '/inquiry/alarms' },



  // ── slice-43 bulk-browse routes (m_configuration master-data tables) ─

  w_task_codes: { to: '/config/task-codes' }, // slice-88: promoted /browse -> /config (Tier-2 form; PB labels `context` as "UOM")
  w_door_setting: { to: '/browse/door-setting', requiredModule: 'ACTIVITIES-YARDMANAGE-MENU' },
  w_trailer_type: { to: '/config/trailer-types', requiredModule: 'CONFIGURATION-YARDMANAGE' }, // slice-50: promoted /browse -> /config
  w_load_plan_type: { to: '/browse/load-plan-type', requiredModule: 'CONFIGURATION-YARDMANAGE' },
  w_pick_doc_form: { to: '/browse/pick-doc-form', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_label_groups: { to: '/browse/label-groups', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_label_def: { to: '/browse/label-def', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_label_actions: { to: '/browse/label-actions', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_label_mapping: { to: '/browse/label-mapping' },
  w_label_types: { to: '/browse/label-types' },
  w_label_printers: { to: '/browse/label-printers', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_label_codes: { to: '/browse/label-codes' },
  w_scales: { to: '/browse/scales', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_pnode_accounts: { to: '/browse/pnode-accounts', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_location_printers: { to: '/browse/location-printers', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_printer_type: { to: '/browse/printer-type', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  // slice-817: PB w_report_types is ONE window with 3 tabs partitioning the
  // same dbxx.report_type table by compliance_flag / email_only Y/N flags.
  // Previously routed to /browse/report-types which showed ALL rows mixed
  // together (PB shows three disjoint subsets the operator switches between).
  // /config/report-types hosts all 3 in TabbedReportTypesPage.
  w_report_types: { to: '/config/report-types', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_report_actions: { to: '/browse/report-actions', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_report_printer_types: { to: '/browse/report-printer-types', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_report_image: { to: '/browse/report-image', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_report_groups: { to: '/browse/report-groups', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_report_printers: { to: '/browse/report-printers' },
  // slice-280, SPLIT slice-3599 (card 138/#3, dev-rules §4.23 one route per menu leaf):
  // w_report_master and w_report_search are two separate PB windows over dbxx.report_master
  // that used to collapse onto one route/mode. w_report_master auto-retrieves
  // (w_report_master.srw:154 ue_retrieve on open); w_report_search waits for the operator to
  // Search (w_search/w_query_base guard). NOTE: neither leaf actually resolves its route via
  // this table — m_reportmaster/m_reportsearch carry no `window` tag in nav.generated.json
  // (the m_phfc_main-ancestor click event parses to nothing), so ID_TO_ROUTE below is the
  // live path; these two rows exist for documentation / any future direct window lookup.
  // w_report_master_submit + w_report_conf + w_report_dwview are child dialogs BOTH parent
  // windows open identically (verified in both .srw files); they stay pointed at the Master
  // grid since neither has its own nav leaf.
  w_report_master: { to: '/browse/report-master' },
  w_report_search: { to: '/browse/report-master-search' },
  w_report_master_submit: { to: '/browse/report-master' },
  w_report_conf: { to: '/browse/report-master' },
  w_report_dwview: { to: '/browse/report-master' },
  // slice-281: w_report_profiles -> generic profile_header (filter by profile_type
  // for the report cluster). Same browse used by other profile-using PB screens.
  w_report_profiles: { to: '/browse/profile-header' },
  // slice-282: report-builder categorization (read-only since the designer is DROPPED).
  w_report_category: { to: '/browse/genrpt-categories' },
  w_report_table_by_category: { to: '/browse/genrpt-category-tables' },
  w_report_column_by_category: { to: '/browse/genrpt-columns' },
  // slice-283: operator-saved report runs + user-report config (same profile_header).
  w_ws_saved_report_search: { to: '/browse/report-image-ws' },
  w_user_report: { to: '/browse/profile-header' },
  // slice-284: PowerView dashboard + web-report stub hubs.
  w_dashboard: { to: '/reports/dashboard' },
  w_web_report: { to: '/reports/web-report' },
  w_qc_rules: { to: '/browse/qc-rules', requiredModule: 'CONFIGURATION-QC' },
  w_zone: { to: '/config/zones' }, // slice-50: promoted /browse -> /config
  w_put_station: { to: '/browse/put-station', requiredModule: 'CONFIGURATION-LOCATION' },
  w_country: { to: '/config/countries' }, // slice-77: promoted /browse -> /config (Tier-2 form)
  w_state: { to: '/config/states', requiredModule: 'CONFIGURATION-ORDER' }, // slice-50: promoted /browse -> /config
  w_pack_code: { to: '/browse/pack-code', requiredModule: 'CONFIGURATION-ORDER' },
  w_machine: { to: '/browse/machine', requiredModule: 'CONFIGURATION-PROCESS' },
  w_labor_code: { to: '/config/labor-codes', requiredModule: 'CONFIGURATION-SYSTEM' }, // slice-50: promoted /browse -> /config
  w_currency: { to: '/config/currencies' }, // slice-50: promoted /browse -> /config
  w_payment_terms: { to: '/browse/payment-terms', requiredModule: 'CONFIGURATION-RECEIPT' },
  w_ship_via: { to: '/browse/ship-via' },
  w_warehouse_auto_num: { to: '/browse/warehouse-auto-num' },
  w_rf_modules: { to: '/browse/rf-modules' },
  w_brand_code: { to: '/config/brand-codes', requiredModule: 'CONFIGURATION-ITEM' }, // slice-50: promoted /browse -> /config
  w_hazmat_code: { to: '/config/hazmat-codes', requiredModule: 'CONFIGURATION-ITEM' }, // slice-50: promoted /browse -> /config
  w_nesting_code: { to: '/config/nesting-codes' }, // slice-83: promoted /browse -> /config (Tier-2 form)
  w_sales_rep: { to: '/config/sales-reps', requiredModule: 'CONFIGURATION-BILLING' }, // slice-84: promoted /browse -> /config (Tier-2 form; note table is salesrep, no underscore)
  w_bill_code: { to: '/config/bill-codes' }, // slice-81: promoted /browse -> /config (Tier-2 form)
  w_gl_codes: { to: '/config/gl-codes', requiredModule: 'CONFIGURATION-BILLING' }, // slice-82: promoted /browse -> /config (Tier-2 form)
  w_email_group: { to: '/config/email-groups', requiredModule: 'CONFIGURATION-EMAIL' }, // slice-95: promoted /browse -> /config (Tier-2 header+detail form; first H+D shape in PH10)
  w_invoice_category: { to: '/browse/invoice-category' },
  w_invoice_activity: { to: '/browse/invoice-activity' },
  w_invoice_company: { to: '/config/invoice-companies', hiddenInPb: true }, // slice-85 port; PB ships m_invoicecompany visible=false (never un-hidden) — mirrored per Chris 2026-07-13
  w_notification: { to: '/config/notify-sets' }, // slice-96: promoted /browse -> /config (H+D form; priority is PK + operator-supplied)
  w_combine_category: { to: '/config/combine-categories', requiredModule: 'CONFIGURATION-ITEM' }, // slice-86: promoted /browse -> /config (Tier-2 form, owner-scoped composite PK); slice-97: H+D upgrade (item_class detail rows)
  w_work_type: { to: '/config/work-types' }, // slice-93: promoted /browse -> /config (Tier-2 form)
  w_work_mode: { to: '/browse/work-mode' },
  w_work_priority_update: { to: '/browse/work-priority-update' },
  w_cyc_category: { to: '/config/cyc-categories', requiredModule: 'CONFIGURATION-WORK' }, // slice-90: promoted /browse -> /config (Tier-2 form)
  w_rtn_grp: { to: '/config/rtn-grps' }, // slice-92: promoted /browse -> /config (Tier-2 form)
  w_rtn_sub_code: { to: '/browse/rtn-sub-code' },
  w_rtn_sub_code_grp: { to: '/browse/rtn-sub-code-grp', requiredModule: 'CONFIGURATION-RECEIPT' },
  w_receipt_type: { to: '/config/receipt-types' }, // slice-89: promoted /browse -> /config (Tier-2 form, 33 fields, 7 sections)
  w_receipt_status_infor: { to: '/browse/receipt-status-infor', requiredModule: 'CONFIGURATION-RECEIPT' },
  w_receipt_step_maint: { to: '/browse/receipt-step-maint', requiredModule: 'CONFIGURATION-RECEIPT' },
  w_receipt_default: { to: '/config/receipt-default', requiredModule: 'CONFIGURATION-RECEIPT' }, // slice-950: form-view (ROGER #502)
  w_order_default: { to: '/config/order-default', requiredModule: 'CONFIGURATION-ORDER' }, // slice-950: form-view (ROGER #506)
  w_order_type: { to: '/config/order-types' }, // slice-87: promoted /browse -> /config (Tier-2 form, 50 fields across 10 sections)
  w_wave_type: { to: '/browse/wave-type' },
  w_va_rules: { to: '/config/va-rules', requiredModule: 'CONFIGURATION-PROCESS' }, // slice-94: promoted /browse -> /config (Tier-2 form; `priotity` typo col deferred, PB binds `priority`); slice-98: H+D upgrade (va_steps detail rows)
  w_qualifier_det: { to: '/browse/qualifier-det', requiredModule: 'CONFIGURATION-ORDER' },
  w_clone_default: { to: '/config/clone-defaults', requiredModule: 'CONFIGURATION-SYSTEM' }, // slice-105: promoted /browse -> /config (H+D form; dw_obj_list surfaced read-only)
  w_division: { to: '/config/divisions', requiredModule: 'CONFIGURATION-ORDER' }, // slice-79: promoted /browse -> /config (Tier-2 form, 3 address blocks; bol_prefix deferred = schema gap)
  w_broker: { to: '/config/brokers', requiredModule: 'CONFIGURATION-ORDER' }, // slice-50: promoted /browse -> /config
  w_consolidator: { to: '/config/consolidators' }, // slice-78: promoted /browse -> /config (Tier-2 form, 2-column SHIP-TO / BILL-TO)
  w_thirdparty: { to: '/config/third-parties', requiredModule: 'CONFIGURATION-ORDER' }, // slice-80: promoted /browse -> /config (Tier-2 form, PB "Third Party Freight Billing")



  // ── slice-44 bulk-browse routes (warehouse + admin + interfaces + config leftovers) ─

  w_location: { to: '/location/new', requiredModule: 'INQUIRY-LOCATION' }, // #1872: PB 'New Location' form (grid retired)
  w_area: { to: '/browse/area' },
  w_station_owner_exclusion: { to: '/browse/station-owner-exclusion', requiredModule: 'CONFIGURATION-ORDER' },
  w_no_pick_items: { to: '/browse/no-pick-items' },
  // slice-815: PB w_shippingtables is ONE window with 19 tabs (Carriers /
  // Methods / Freight Classes / Freight Terms / Shipping Workstation / Ship
  // Lanes / Shipping Services / Shipping Via Codes / Rate Shopping / Ship
  // Method Translation / Back Order Reset Ship Method / Importer Facility /
  // Broker Mapping / Importer Mapping / Carrier Tracking URLs / Ship
  // Connections / Ship Engines / EOD Manifest Carriers / Ship Engine
  // Carrier). Previously routed to /browse/shippingtables which only surfaced
  // ONE of those tabs (service_payment_types); 6 others were scattered under
  // sibling routes and 12 were unported. /config/shipping-tables hosts all 19
  // in TabbedShippingTablesPage. Per-tab browse cfgs remain registered as data
  // endpoints but are not directly nav-reachable from here.
  w_shippingtables: { to: '/config/shipping-tables' },
  w_fq_insur_carrier_maint: { to: '/browse/fq-insur-carrier-maint' },
  w_case_coded_states: { to: '/browse/case-coded-states', requiredModule: 'CONFIGURATION-ORDER' },
  w_ship_importer: { to: '/browse/ship-importer', requiredModule: 'CONFIGURATION-ORDER' },
  w_order_add_on_rule: { to: '/browse/order-add-on-rule' },
  w_whs_process_code: { to: '/browse/whs-process-code', requiredModule: 'CONFIGURATION-PROCESS' },
  w_process_rules: { to: '/browse/process-rules', requiredModule: 'CONFIGURATION-PROCESS' },
  w_mask: { to: '/browse/mask', requiredModule: 'CONFIGURATION-SYSTEM' },
  // slice-3020 (#2126, §4.23): w_configuration is TWO mode-locked routes (one per PB menu
  // leaf — m_sysconfig '(sys)' m_main.srm:38043-38044 → /browse/configuration; the Window >
  // User Setting leaf m_usersetting_phfc '(usr)' m_main.srm:7455-7456 →
  // /browse/configuration-user, injected into the catalog via EXTRA_ROUTES because the
  // Window menu is dropped from the sidebar). This window-level fallback stays on the
  // (sys) leaf; see ID_TO_ROUTE below.
  w_configuration: { to: '/browse/configuration' },
  w_rf_menu_config: { to: '/browse/rf-menu-config' },
  // Wizards never made it out of dev in PH10; Bob OK'd disabling it 2026-07-16. Route kept
  // registered (code preserved), dropped from the sidebar + tester queue via devDisabled.
  w_phfc_wizard_header: { to: '/browse/phfc-wizard-header', devDisabled: true },
  w_window_set: { to: '/browse/window-set', requiredModule: 'CONFIGURATION-SYSTEM' },
  w_if_tran_code: { to: '/browse/if-tran-code', requiredModule: 'CONFIGURATION-INTERFACE' },
  w_production_lines: { to: '/browse/production-lines', requiredModule: 'CONFIGURATION-PRODUCTION' },
  w_location_type_return: { to: '/browse/location-type-return' },
  w_translate_rf_help: { to: '/browse/translate-rf-help' },
  w_crt_language: { to: '/browse/crt-language' },
  w_translate_rf_msg: { to: '/browse/translate-rf-msg' },
  w_container_item: { to: '/browse/container-item' },
  w_price_change_review: { to: '/browse/price-change-review', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_apparel_color: { to: '/browse/item-apparel-color', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_apparel_dim_pack: { to: '/browse/item-apparel-dim-pack', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_apparel_label: { to: '/browse/item-apparel-label', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_class_combine: { to: '/browse/item-class-combine' },
  w_item_customer_custom: { to: '/browse/item-customer-custom', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_tax_class: { to: '/browse/item-tax-class', requiredModule: 'CONFIGURATION-ITEM' },
  w_pre_pack_item_build: { to: '/browse/pre-pack-item-build', requiredModule: 'CONFIGURATION-ITEM' },
  w_harmonized_codes: { to: '/browse/harmonized-codes' },
  // #2514 / needs-chris card 27 (Chris 2026-08-05: "full PB fidelity, so option b").
  // w_item_master is referenced by exactly ONE leaf in the whole PB menu source —
  // m_newitem "&New Item" (m_main.srm:32877, window_name="w_item_master (insert)",
  // boolean1="Y"); the window's open event has only two SINGLE-RECORD modes (insert:
  // wf_title("New Item") + dw_1.insertrow(0), w_item_master.srw:893-896; else
  // wf_title("Item Maintenance: …") + a one-row retrieve, :902/:1749). PB has NO
  // item-master search grid — item searching is w_item_search (m_main.srm:17005),
  // ported at /browse/items-search. The PH10-invented /browse/item-master grid is
  // RETIRED (router redirects it); this mapping now names the window's real port,
  // the ItemMasterPage insert/maintenance form. Keeping it on the retired path would
  // also break the "nav targets are never redirect sources" pin in navItemRoutes.test.
  w_item_master: { to: '/items/master' },
  w_item_udi: { to: '/browse/item-udi', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_class_group: { to: '/browse/item-class-group', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_configuration_profile: { to: '/browse/item-configuration-profile' },
  w_cubiscan: { to: '/browse/cubiscan' },
  w_item_cfg_batch_clone: { to: '/browse/item-cfg-batch-clone' },
  w_style_color: { to: '/browse/style-color', requiredModule: 'CONFIGURATION-ITEM' },
  w_size_type_maint: { to: '/browse/size-type-maint', requiredModule: 'CONFIGURATION-ITEM' },
  w_coa_translation: { to: '/browse/coa-translation', requiredModule: 'CONFIGURATION-ITEM' },
  w_item_class_pack_prio: { to: '/browse/item-class-pack-prio', requiredModule: 'CONFIGURATION-ITEM' },
  w_owner_count_frequency_by_velocity: { to: '/browse/owner-count-frequency-by-velocity', requiredModule: 'CONFIGURATION-ITEM-MENU' },
  w_config_report_category: { to: '/browse/config-report-category', requiredModule: 'CONFIGURATION-MENU' },
  w_config_report_tables_all: { to: '/browse/config-report-tables-all' },
  w_config_report_joins_tables: { to: '/browse/config-report-joins-tables', requiredModule: 'CONFIGURATION-MENU' },
  // Work-pool consolidation (docs/design/work-pool-consolidation.md): the canonical
  // screen is /manage/work-pool; /browse/work-pool redirects there.
  w_work_pool: { to: '/manage/work-pool' },
  // slice-799: PB truck-activity-support cluster (dock-manager-door, truck-schedule,
  // truck-inout-search) opens this Pick Location Summary as a sub-window.
  w_truck_picklocsum: { to: '/truck-picklocsum' },
  w_auto_process_timer: { to: '/browse/auto-process-timer' },
  w_auto_process_server_maint: { to: '/browse/auto-process-server-maint' },
  w_mover_order_request: { to: '/browse/mover-order-request' },
  w_label_request: { to: '/browse/label-request' },
  w_report_request: { to: '/browse/report-request' },
  w_email_requests: { to: '/browse/email-requests' },
  w_rfmsg_center: { to: '/browse/rfmsg-center' },
  w_global_item_maint: { to: '/browse/global-item-maint', requiredModule: 'MANAGE-WAREHOUSE-REWAREHOUSING' },
  w_global_location_maint: { to: '/browse/global-location-maint' },
  w_season_assign: { to: '/browse/season-assign' },
  // slice-822: PB w_lock is ONE window with 8 tabs showing what's currently
  // locked in the system (Locations/Pallets/Cartons/Contents/Restocks/Work/
  // Stations/Orders). Tabs 1-4 partition dbxx.locks by lock_type; tabs 5-6
  // surface mover-locked rows in rstk_request/work_pool; tabs 7-8 query
  // dbxx.locks_station/dbxx.locks_order. Previously routed to /browse/lock
  // which surfaced only the bare locks dump.
  w_lock: { to: '/manage/system-locks' },
  w_clear_ctr: { to: '/browse/clear-ctr', requiredModule: 'ADMINISTRATOR-COUNTERS' },
  w_rtn_day_end: { to: '/browse/rtn-day-end', requiredModule: 'ADMINISTRATOR-RETURN-DAY-END' },
  w_calc_server: { to: '/config/calc-server' }, // slice-949: form-view (ROGER #505), promoted /browse -> /config
  w_purge_tran: { to: '/browse/purge-tran' },
  w_interface_services_types_tables: { to: '/browse/interface-services-types-tables' },
  w_interface_services_types: { to: '/browse/interface-services-types', requiredModule: 'INTERFACES-CONFIGURATION' },
  w_interface_servers: { to: '/browse/interface-servers' },
  w_interface_services: { to: '/browse/interface-services', requiredModule: 'INTERFACES-MAINTENANCE' }, // H-028: window_modules w_interface_services (admin)
  w_gp_owner_site: { to: '/browse/gp-owner-site', requiredModule: 'INTERFACES-CONFIGURATION' },



  // ── slice-45 bulk-browse routes (m_activities batch) ─────────────────

  // Slice-430: PB w_pallet_back_to_stock is a pallet-level data-entry
  // workflow (same shape as w_inv_back_to_stock, fewer fields). New page
  // at /inventory/pallet-back-to-stock; legacy /browse route removed from
  // the menu but cfg/BE entry stay for direct URL access.
  w_pallet_back_to_stock: { to: '/inventory/pallet-back-to-stock' },
  w_prod_lot_assign: { to: '/browse/prod-lot-assign' },
  w_dock_manager_door: { to: '/browse/dock-manager-door' },
  w_global_order_maint: { to: '/browse/global-order-maint', requiredModule: 'ACTIVITIES-OP-ORDER-ADJUST' },
  w_global_order_line_maint: { to: '/browse/global-order-line-maint' },
  w_delete_open_orders: { to: '/browse/delete-open-orders' },
  w_deleted_orders: { to: '/browse/deleted-orders' },
  w_order_reallocation: { to: '/browse/order-reallocation' },
  w_truck_sequence: { to: '/orders/truck-load-waving' },
  w_picked_consolidation: { to: '/browse/picked-consolidation' },
  w_truck_load_carton_to: { to: '/browse/truck-load-carton-to' },
  w_carton_move: { to: '/browse/carton-move' },
  w_truck_unload_carton_from: { to: '/browse/truck-unload-carton-from' },
  w_work_adjustment: { to: '/browse/work-adjustment', requiredModule: 'ACTIVITIES-RESTOCKING-MENU' },
  w_appoint_dock: { to: '/browse/appoint-dock', requiredModule: 'ACTIVITIES-YARDMANAGE-MENU' },
  w_appoint_prod_line: { to: '/browse/appoint-prod-line', requiredModule: 'ACTIVITIES-PRODUCTION-ADMIN' },
  w_appoint_kit: { to: '/browse/appoint-kit', requiredModule: 'ACTIVITIES-KIT' },
  // FB#52 (ROGER, slice-354): w_pre_receipt is the manual-CREATE form for a new
  // pre-receipt (wf_title 'Pre-Receipt' per w_pre_receipt.srw L408). NOW points to
  // the dedicated create page; w_pre_receipt_search (search-style list) still routes
  // to /browse/pre-receipts. The two menu items had been collapsed to the search
  // list as a slice-332 stop-gap; this restores the CREATE behavior ROGER expects.
  // Slice-496 fix: slice-354 invented module 'MANAGE-RECEIPT' which doesn't
  // exist in dbxx.modules -- the gate silently hid the menu item for everyone
  // and the @RequiresModule on the controller would have 403'd any click.
  // Correct module per dbxx.modules.description: ACTIVITIES-RECEIPT-ENTRY
  // ("Receipt Entry"). Matches receipt-create + global-receipt-maint cluster.
  w_pre_receipt: { to: '/receipts/pre-receipt-new', requiredModule: 'ACTIVITIES-RECEIPT-ENTRY' },
  // H-052: dbxx.window_modules_act w_backorder_request → ACTIVITIES-OP-ORDERS-WAVING.
  w_backorder_request: { to: '/browse/backorder-request', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' },
  w_global_receipt_maint: { to: '/browse/global-receipt-maint' },
  w_ship_dayend: { to: '/browse/ship-dayend' },
  w_close_receipt_by_date: { to: '/browse/close-receipt-by-date', requiredModule: 'ACTIVITIES-RECEIPT-INQUIRY' },



  // ── slice-47 bulk-browse routes (m_activities round 2) ──────────────────

  // scope-and-navmods (2026-10-12): was 'MANAGE-WORK', which is not a dbxx.modules id, so the
  // item was hidden from everyone. PB's module for w_pa_request (window_modules_act
  // .actual_module_id = default_module_id, Chris_900v2).
  w_pa_request: { to: '/putaway-errors', requiredModule: 'ACTIVITIES-RECEIPT-ADMIN' },
  w_receipt_maint: { to: '/browse/receipt-maint' },
  // FB#121 (ROGER) / slice-376: PB w_receipt_search is the list screen the
  // /browse/receipt-maint cfg now mirrors (was previously mis-tagged
  // w_receipt_maint). Aliased so the search menu item lights up correctly.
  // The single-record w_receipt_maint maintenance form is a Tier-2 port deferred
  // to a future slice; once it lands, w_receipt_maint moves to its own route.
  w_receipt_search: { to: '/browse/receipt-maint' },
  w_rcv_pa: { to: '/browse/rcv-pa' },
  w_kit_building: { to: '/browse/kit-building' },
  // slice-819: PB w_qc_check is QC Inspect — an interactive 4-pane operator
  // workflow (scan container → see inventory → mark P/F per qc_step → submit
  // qc_activity + det rows + optional deplete). Previously routed to
  // /browse/qc-check which surfaced unrelated dbxx.contents rows; full rebuild
  // at /qc/inspect with dedicated controller (QcInspectController).
  w_qc_check: { to: '/qc/inspect' },
  w_vas_request: { to: '/browse/vas-request' },
  w_order_maint: { to: '/orders/search', requiredModule: 'ACTIVITIES-OP-ORDERS-ADMIN' },
  w_order_delivery_proof: { to: '/browse/order-delivery-proof' },
  w_carton_confirm: { to: '/browse/carton-confirm' },
  w_carton_pickpack_ship: { to: '/browse/carton-pickpack-ship' },
  w_carton_pickverify_ship: { to: '/browse/carton-pickverify-ship' },
  w_carton_pickpack_by_store: { to: '/browse/carton-pickpack-by-store', requiredModule: 'ACTIVITIES-OP-CARTONS' },
  w_carton_move_by_ord: { to: '/browse/carton-move-by-ord' },
  w_merge_order_pallet: { to: '/browse/merge-order-pallet' },
  w_asncartonpack: { to: '/browse/asncartonpack' },
  w_value_add: { to: '/browse/value-add' },
  w_value_add_pick: { to: '/browse/value-add-pick' },
  w_auto_rstk: { to: '/restock/auto-restock' },
  w_labor_task_assign: { to: '/browse/labor-task-assign', requiredModule: 'ACTIVITIES-LABOR-MANAGEMENT' },
  w_verify: { to: '/browse/verify' },
  w_shorty: { to: '/browse/shorty' },
  w_label_view: { to: '/browse/label-view' },
  w_labelgen: { to: '/browse/labelgen' },



  // ── slice-48 bulk-browse routes (round 7) ────────────────────────────
  w_hi_alarms: { to: '/browse/adm-alarms' },

  w_ship_shipping: { to: '/browse/ship-shipping' },
  w_kit_building_batch_single_serial_bom: { to: '/browse/kit-building-batch-single-serial-bom', requiredModule: 'ACTIVITIES-KIT-BUILD' },
  w_bill_summarize: { to: '/browse/bill-summarize' },
  w_activity_user_group: { to: '/browse/activity-user-group', requiredModule: 'ACTIVITIES-LABOR-MANAGEMENT' },
  w_multi_src_pickpack_ship: { to: '/browse/multi-src-pickpack-ship' },
  w_verify_w_ship: { to: '/browse/verify-w-ship' },
  w_order_fastpick_build_1_m: { to: '/browse/order-fastpick-build-1-m' },
  w_order_fastpicktruck: { to: '/browse/order-fastpicktruck' },
  w_order_fastpick_ship: { to: '/browse/order-fastpick-ship' },
  // slice-3013 (§4.23): w_carton_repack_1_m is TWO mode-locked routes (one per PB menu
  // leaf — Chris platform ruling 2026-07-21). This window-level fallback points at the
  // repack leaf (first in m_main.srm); the catalog pins its title and injects the
  // pick-and-pack sibling row (scripts/generate-page-catalog.mjs). The legacy
  // /browse/carton-repack-1-m route redirects.
  w_carton_repack_1_m: { to: '/browse/multi-carton-repack', requiredModule: 'ACTIVITIES-OP-CARTONS' },
  w_carton_repack_ship_sio: { to: '/browse/carton-repack-ship-sio' },
  w_routing_order_tag: { to: '/browse/routing-order-tag' },
  w_ym_truck_rec_mode_header: { to: '/browse/ym-truck-rec-mode-header', requiredModule: 'CONFIGURATION-YARDMANAGE' },
  w_ym_truck_rec_mode_factor: { to: '/browse/ym-truck-rec-mode-factor', requiredModule: 'CONFIGURATION-YARDMANAGE' },
  w_report_action_allowed_methods: { to: '/browse/report-action-allowed-methods', requiredModule: 'CONFIGURATION-LABELS/REPORTS' },
  w_ephs_jsps: { to: '/browse/ephs-jsps' },

  // ── slice-49 autonomous-batch (placeholder burn-down) ─────────────────
  // 13 placeholders promoted to live routes across 5 commits. Two patterns:
  // (1) PROMOTE -- the scaffolder page already existed, just wire the PB window.
  // (2) NEW BROWSER -- registered in TableBrowserRegistry + browseConfigs.
  // These entries live ABOVE the auto-generated marker so the regen treats them
  // as wired and excludes them from the auto-block.
  w_domain: { to: '/config/domains', requiredModule: 'CONFIGURATION-LOCATION' }, // promote: DomainPage (Configuration > Location > Domains)
  w_conversion: { to: '/config/conversions', requiredModule: 'CONFIGURATION-SYSTEM' }, // promote: ConversionPage (Configuration > System > Conversions)
  w_seasonclass: { to: '/browse/season-class', requiredModule: 'CONFIGURATION-ITEM' }, // browser (Configuration > Item > Season Classes)
  w_loc_wait: { to: '/browse/loc-wait' }, // browser WAREHOUSE (Manage Warehouse > Manage Work > Work Release Locations)
  w_devices: { to: '/browse/devices', requiredModule: 'CONFIGURATION-LOCATION' }, // browser (Configuration > Location > Devices)
  w_building: { to: '/browse/buildings', requiredModule: 'CONFIGURATION-LOCATION' }, // browser (Configuration > Location > Buildings)
  w_ee_translation: { to: '/browse/ee-translation' }, // browser (Interfaces > eEnterprise > Defaults > Translation)
  w_inevconfig: { to: '/browse/inev-config', hiddenInPb: true }, // PB ships m_eventcomponentconfiguration visible=false (never un-hidden) — mirrored per Chris 2026-07-13
  w_prox: { to: '/browse/proximities' }, // browser WAREHOUSE CRUD (Configuration > Location > Proximities)
  w_prox_grid: { to: '/browse/proximity-grid', requiredModule: 'CONFIGURATION-LOCATION' }, // browser WAREHOUSE read-only (Configuration > Location > Proximity Grid)
  w_sys_server: { to: '/browse/sys-server', requiredModule: 'INTERFACES-MAINTENANCE' }, // browser NONE save+delete (Interfaces > System Server Maintenance)
  w_intg_ship_header_dwn: { to: '/browse/intg-ship-dwn' }, // browser BOTH read-only (Interfaces > Shipping > Ship Download)
  // w_pb_ship_dwn removed slice-51: PB integration dropped (Bob, 2026-05-18)

  // ── slice-50 (questions-log answers) ──────────────────────────────────
  // Two admin log-table browsers per Chris's Q1: NONE scope matches PB.
  // No required module today (matches PB's open access); defensive gate
  // would be added if a security audit calls for it.
  w_phs_errors: { to: '/browse/adm-phs-errors' }, // System Error Log (save+delete)
  w_unlock_log: { to: '/browse/adm-unlock-log', requiredModule: 'MANAGE-WAREHOUSE-LOGS' }, // Unlock Log (read-only)

  // ── slice-50 batch 2: security tables w/ change-log audit-tracked CRUD ────
  // ChangeLogService is already wired into TableBrowserWriteController; these
  // 3 entries enable audit-trail-tracked CRUD by declaring .changeLogType()
  // in the registry. PB change_log_type strings preserved verbatim.
  w_usr_grp: { to: '/browse/sec-usr-grp' }, // User Groups (audit-tracked CRUD)
  w_modules: { to: '/browse/sec-modules' }, // Modules (audit-tracked CRUD)
  w_window_modules: { to: '/browse/sec-window-modules' }, // Window Modules (audit-tracked CRUD)

  // ── slice-50 batch 3: prox_segments w/ Calculate Distances action (BrowseAction MVP) ────
  w_prox_segments: { to: '/browse/prox-segments', requiredModule: 'CONFIGURATION-LOCATION' }, // Proximity Segments + 'prox-segments.calc' action
  w_vendor_qc_update_rule: { to: '/browse/vendor-qc-update-rule', requiredModule: 'MANAGE-WAREHOUSE-VENDOR-QC' }, // CFG mode — Vendor QC Update Rule (CRUD). ADMIN mode is keyed by menu id 'm_vendorqcupdate' in ID_TO_ROUTE below.

  // ── slice-52: Carton Processing cluster -- foundational screen ────────
  // First screen of the Carton Processing cluster (Activities > Order Processing >
  // Carton Processing). The launcher is a scan-routing dispatcher: operator scans a
  // carton/pallet/item barcode, the BE SP decides which downstream window to open,
  // the FE navigates there. Establishes the reusable <ScanField> + useLauncherDispatch
  // patterns that subsequent carton screens will build on.
  w_carton_activity_launcher: { to: '/cartons/activity-launcher', requiredModule: 'ACTIVITIES-OP-CARTONS' },

  // ── slice-3110: Carton Doc Reprint — scan-a-work-carton reprint utility ───
  // Activities > Order Processing > Carton Processing > Carton Doc Reprint
  // (m_cartondocreprint, m_main.srm:11069-11079). Reuses the shared
  // uo_screen_carton_pack_support reprint object; Print stays a no-op per the
  // printing-stub-as-future policy (the PB source's own reprint chain is
  // block-commented — see CartonDocReprintService javadoc).
  w_work_carton_doc_reprint: { to: '/cartons/doc-reprint' },

  // ── slice-54: Carton Merge and Ship -- first ship-flow screen ─────────
  // Operator scans pre-packed cartons, the FE adds each to a merge list with order
  // context auto-populated from work_pool_detail. Scale reading (mock adapter, ADR-0002),
  // Ship button routes through ShipSysService (mock adapter, ADR-0004). First screen to
  // exercise the slice-53 infrastructure end-to-end.
  w_carton_merge_ship: { to: '/cartons/merge-ship' },

  // ── slice-55: Carton Overpack and Ship ────────────────────────────────
  // Two-stage variant of merge-ship: inner cartons collect; operator clicks Pack Box
  // to commit them into an outer box with its own weight + dimensions; multiple outer
  // boxes can be packed per session before Ship submits them all. Each outer box gets
  // one tracking number from ShipSys. Reuses cartons.lookup-carton from merge-ship.
  w_carton_overpack_ship: { to: '/cartons/overpack-ship', requiredModule: 'ACTIVITIES-OP-CARTONS' },

  // ── slice-56: Carton Overpack and Ship for Border Free ────────────────
  // Borderfree consolidator variant (active per RB, 2026-05-18 SME survey).
  // Single-stage scan-and-ship: PB hard-overwrites s_company='Borderfree' and writes
  // to dbxx.misc_shipments with ship_type='BORDERFREE'. One outer tracking number per
  // submit regardless of how many inner cartons.
  w_carton_overpack_ship_bfree: { to: '/cartons/borderfree-ship', requiredModule: 'ACTIVITIES-OP-CARTONS' },

  // ── slice-57: Carton Recombine and Ship ──────────────────────────────
  // Exception/recovery flow: transfer contents from one or more source cartons into a
  // single destination carton (operator-provided or auto-generated via the equivalent
  // of fsp_ship_seq). Ships as one shipment. PB uses fsp_repack_crt for content
  // transfer; that side effect lands when a real ShipSys adapter replaces the mock.
  w_carton_recombine_ship: { to: '/cartons/recombine-ship' },

  // ── slice-58: Put to Store ───────────────────────────────────────────
  // Retail put-to-store wall (wave-driven). Operator selects a wave, system shows
  // one row per store with pieces needed, each item scan tells the operator which
  // store cubby to drop into. Slice-58 is read-only -- the commit logic
  // (work_pool_detail updates, fsp_cop_conf) lands alongside w_carton_repack which
  // shares the same machinery.
  w_put_to_store: { to: '/cartons/put-to-store' },

  // ── slice-59: Carton Process (supervisor admin) ─────────────────────
  // Search-and-act admin screen on work_cartons. Slice-59 ships Search + Void;
  // Reprint, Release Hold, and Ship-from-this-screen are deferred (need
  // label-print + hold-release infrastructure). ShipSysService gained voidPackage.
  w_carton_process: { to: '/cartons/process' },
  w_carton_induction: { to: '/cartons/induction' }, // Activities > Order Processing > Carton Induction (#849 port wave 1)

  // ── slice-61: Carton Repack (4-mode workhorse) ──────────────────────
  // One PB window backs 4 menu entries via is_work_mode: 'repack' / 'pick pack' /
  // 'pick pack check' / 'pick pack carton scan'. PH10 surfaces the mode picker at
  // the top of the page. Uses PickPackService.confirmPicks (mock; ADR-0005).
  // The real adapter routes by workMode to fsp_cop_conf / fsp_pop_conf / fsp_repack_crt.
  w_carton_repack: { to: '/cartons/repack', requiredModule: 'ACTIVITIES-OP-CARTONS' },

  // ── slice-62: Carton Pick Pack Ship -- Single Item Order ────────────
  // PB's biggest carton window (5285 lines). Single-item-order fast path:
  // combined pick + ship in one operator action. BE composes
  // PickPackService.confirmPicks + ShipSysService.shipCartons atomically.
  // Closes out the carton cluster: 9/9 PB carton screens now have PH10 routes.
  w_carton_pickpack_ship_sio: { to: '/cartons/pickpack-ship-sio' },

  // ── slice-63: Ship Void (Shipping cluster) ──────────────────────────
  // Scan a shipped carton, view its status, void via ShipSys.voidPackage.
  // Reuses cartons.process.void BE action from slice-59. New shared lookup
  // shipping.lookup-shipped-carton (work_cartons by exact carton_id) will be
  // reused by subsequent Shipping cluster screens.
  w_ship_void: { to: '/ship/void' },

  // ── slice-64: Rate Shopping for Order ────────────────────────────────
  // Operator enters order + package, gets sorted carrier quotes from
  // ShipSysService.rateShop (new method this slice). Mock returns UPS/FEDEX/USPS
  // deterministic quotes; real adapter fans out to RateLinx / Nexxio / direct.
  // Applying a selected quote back to the order is deferred (needs an
  // orders.update-ship-method action).
  w_rateshop_order: { to: '/ship/rateshop' },

  // ── slice-65: Misc Shipping (free-form / no-order) ───────────────────
  // Operator hand-enters ship-to / bill-to / return addresses, picks carton type +
  // ship method, captures weight/dims, submits. PB writes to dbxx.misc_shipments;
  // mock returns synthetic tracking. Targets parcel-only carriers.
  w_ship_misc: { to: '/ship/misc' },

  // ── slice-66: Shipping Batch Process ────────────────────────────────
  // Multi-carton wave-scoped batch shipping. Reuses cartons.merge-ship.submit BE
  // action verbatim -- the adapter doesn't care whether cartons came from one
  // order or many. wave_seq_num field for traceability.
  w_ship_batch: { to: '/ship/batch', requiredModule: 'ACTIVITIES-SHIPPING-WORKER' },

  // ── slice-67: Ship By Carton / Cart ─────────────────────────────────
  // Single-carton ship path (slice-67 scope). Cart-based enumeration is deferred.
  // Reuses cartons.merge-ship.submit with a one-element cartons list.
  w_ship_by_carton: { to: '/ship/by-carton' },

  // ── slice-68: Ship Multi-Carton Process ─────────────────────────────
  // N cartons under shared header; each row keeps its own weight (not a single
  // total). Reuses cartons.merge-ship.submit.
  w_ship_multi_cartons: { to: '/ship/multi-cartons', requiredModule: 'ACTIVITIES-SHIPPING-WORKER' },

  // ── slice-69: Day End for Open Manifests ────────────────────────────
  // End-of-day carrier manifest close. 4 modes (Standard / Trailer / UPS World
  // Ease / FedEx Master). New ShipSysService.dayEnd method. Auto-ship/auto-void
  // sweep before close deferred.
  w_ship_dayend_open: { to: '/ship/dayend' },

  // ── slice-70: Re-Process Shipping Errors ────────────────────────────
  // Conveyor-interface error queue. Lists if_conveyor_dwn rows with non-zero
  // if_status, lets operator retry one at a time via cartons.merge-ship.submit.
  w_ship_reprocess_error: { to: '/ship/reprocess-error' },

  // ── slice-71: Shipping Station (marquee, Shipping cluster closeout) ─
  // PB's biggest shipping window (5285 lines). Scan-to-ship single-carton UI with
  // scale + rate-shop + ship + void inline. No new BE actions -- composes
  // cartons.lookup-carton + shipping.rate-shop + cartons.merge-ship.submit +
  // cartons.process.void. Closes the Shipping cluster: 9/9 PB shipping screens.
  w_ship_station: { to: '/ship/station' },

  // ── slice-72: Configuration browser blitz (round 1) ────────────────
  // Four tabbed PB windows -> 11 backing table-browsers. Each PB window routes to
  // its first tab; sibling tabs reachable via tab strip (browseConfig `tabs` array).
  // All NONE-scope CRUD config tables. New BrowseConfig.tabs feature added in
  // browseConfigs.ts -- TableBrowserPage pipes it through PageLayout's tabs slot.
  w_container_condition: { to: '/browse/cfg-pallet-cond', requiredModule: 'CONFIGURATION-CONTAINER' },
  w_rules: { to: '/browse/cfg-pa-rule' },
  // cfg-pa-rule rebuild: the w_rules zoom children are rule-keyed pages
  // (/config/rules/<type>/<rule>); PB opens them only from a w_rules row, so the
  // window names collapse to their parent tab (sub-window issue linking, #606).
  w_putprio: { to: '/browse/cfg-pa-rule' },
  w_pickprio: { to: '/browse/cfg-pick-rule' },
  w_restprio: { to: '/browse/cfg-rstk-rule' },
  w_storage_class: { to: '/browse/cfg-pa-class', requiredModule: 'CONFIGURATION-RULES' },
  w_storage_groups: { to: '/browse/cfg-pa-grp', requiredModule: 'CONFIGURATION-RULES' },

  // ── slice-73: Configuration browser blitz (round 2) ────────────────
  // Three more tabbed windows -> 17 backing table-browsers.
  // slice-823: PB w_container_type is ONE window with 6 tabs over 6 container
  // tables (pallet_type/carton_type/tote_type/carton_type_byslot/carton_group/
  // carton_category). Previously routed to /browse/cfg-pallet-type (only 1 of
  // 6 tabs). All 6 cfgs were already registered; /config/container-types now
  // hosts them in TabbedContainerTypePage. Sibling browse routes still resolve.
  w_container_type: { to: '/config/container-types' },
  // slice-821: PB w_reasons is ONE window with 7 tabs over different reason_*
  // tables (Adjust/Hold/Block/Exception Type/Return/Pre Rec/Activity).
  // Previously routed to /browse/cfg-reason-adjusted (only 1 of 7 tabs). All 7
  // cfgs were already registered; /config/reasons now hosts them in
  // TabbedReasonsPage. Sibling browse routes still resolve as data endpoints.
  w_reasons: { to: '/config/reasons' },
  // slice-3011: deep-link/catalog FALLBACK only — w_productivity_config is a single-grid
  // window with FOUR launch modes (gstr_winparm.string1 dept/shift/job/equip,
  // w_productivity_config.srw open), one per PB menu leaf. All four menu ids override via
  // ID_TO_ROUTE below (m_departmentmaintenance / m_shiftmaintenance / m_jobmaintenance /
  // m_equipment); this entry only catches bare-window references, resolving to the first
  // mode (dept). The old tabbed "Productivity Configuration" umbrella was invented — PB
  // has no tabs (un-tabbed in slice-3011).
  w_productivity_config: { to: '/browse/cfg-department' },

  // ── slice-74: Configuration translation suite ──────────────────────
  // Four PB windows -> 9 backing crt_*translate*-family tables.
  w_translate_maint: { to: '/browse/cfg-translate' },
  // cfg-ctranslate1 rebuild: route through the hub-audited /browse/cfg-ctranslate1
  // path, which is now statically shadowed to ColumnTranslatePage (the faithful
  // w_translate_column editor; /config/column-translations still resolves too).
  w_translate_column: { to: '/browse/cfg-ctranslate1' },
  w_translate_msg: { to: '/browse/cfg-mtranslate1' },
  w_translate_menu: { to: '/browse/cfg-menu-translate' },

  // ── slice-75: globin browsers + schema-gap partials ────────────────
  // Two full ports (w_globin_filters / w_globin_profiles -- both header+detail
  // tabbed) + two partial ports (w_archive_configuration / w_qc_rate -- only
  // the tabs whose backing tables exist in the snapshot are wired; the missing
  // tabs surface as scope notes in subtitle until schema is confirmed).
  w_globin_filters: { to: '/browse/cfg-globin-filters' },
  w_globin_profiles: { to: '/browse/cfg-globin-profiles', requiredModule: 'CONFIGURATION-LOCATION' },
  w_archive_configuration: { to: '/browse/cfg-purge-configuration', requiredModule: 'CONFIGURATION-SYSTEM' },
  // slice-814: w_qc_rate is ONE PB window with 4 tabs (User / Order Type /
  // Pick Rule / Customer). PH10 hosts them in a single TabbedQcRatePage at
  // /config/qc-rates — the 4 cfg-*-qc-rate browse cfgs remain registered for
  // API access but are not separate nav routes.
  w_qc_rate: { to: '/config/qc-rates', requiredModule: 'CONFIGURATION-QC' },

  // ── slice-76: Global Price Change (PB tool window) ─────────────────
  // BrowseActionRegistry actions config.price-change-global.{preview,apply} drive a
  // bulk update of item_master.price1..4 + an audit row per item in price_change_log.
  // Promoted from placeholder; see PriceChangeGlobalPage.tsx for the operator flow.
  w_price_change_global: { to: '/config/price-change-global' },

  // ── slice-100: eEnterprise main interface browsers ─────────────────
  // Four PB windows, each with Active + History tabs (8 browse keys total).
  // Route points at the Active tab; the History sibling is reachable via the
  // tab strip in TableBrowserPage. Move-to-history / reset-errors menu
  // actions are surfaced as placeholder actions -- need SP-backed batch
  // endpoints to wire fully (deferred follow-up).
  w_ee_adjustments: { to: '/browse/ee-adjustments' },
  w_ee_asns: { to: '/browse/ee-asns', requiredModule: 'INTERFACES-CONFIGURATION' },
  w_ee_orders: { to: '/browse/ee-orders' },
  w_ee_receipts: { to: '/browse/ee-receipts' },

  // ── slice-101: w_postdeduct (Post Deduct History viewer) ───────────
  // 4-tab browser across PostDeduct_01/02/Hist_01/Hist_02. Read-only;
  // "Purge History" action surfaced as placeholder.
  w_postdeduct: { to: '/browse/post-deduct-01' },

  // ── slice-103: w_if_inventory_balance ───────────────────────────────
  // Single-SP-runner page. BrowseActionRegistry action
  // `interfaces.inv-balance.run` wraps dbxx.if_inv_balance(usr_id,
  // warehouse_id, owner_id) -- rebuilds the inventory-balance snapshot.
  w_if_inventory_balance: { to: '/interfaces/inv-balance', requiredModule: 'INTERFACES-MAINTENANCE' },

  // ── slice-104: w_rtn_request (Return Request list) ─────────────────
  // Read-only browse over dbxx.return_request. Delete via fsp_rtn_req_delete
  // is a placeholder action -- next slice wires the row-context BrowseAction.
  w_rtn_request: { to: '/browse/rtn-request' },

  // ── slice-108: w_putwall_view_by_batch (read-only inquiry) ─────────
  // Custom inquiry page; backend re-implements the dynamic-WHERE PB SP body
  // as parameterized SQL (read-side hand-rolled SQL per dev-rules).
  w_putwall_view_by_batch: { to: '/orders/putwall-batches', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' },

  // ── slice-109: w_receipt_line_search (read-only inquiry) ───────────
  // Custom inquiry; backend re-implements PB's dynamic-WHERE SP as
  // parameterized SQL over receipt_lines or hi_receipt_lines. Ancient
  // source deferred to w_ancient ADR.
  w_receipt_line_search: { to: '/receipts/line-search' },



































  // ── salvaged promoted entries ──────────────────────────────────────────
  // 28 entry/entries were promoted inline (no longer /pb/...)
  // while still inside the auto-generated section. Moved here on the most
  // recent `build-placeholder-routes.mjs` run so the regen does not clobber
  // them. Safe to leave or relocate by hand.
  w_assign_batch_paper_pick_finish: { to: '/orders/paper-pick-assign/finish', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' }, // Activities > Order Processing > Orders and Waving > Batch Paper Pick Assigment Finish
  w_assign_batch_paper_pick_start: { to: '/orders/paper-pick-assign/start', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' }, // Activities > Order Processing > Orders and Waving > Batch Paper Pick Assigment Start
  w_bill_manual: { to: '/billing/manual' }, // Activities > Billing > Manual Billing
  w_chold: { to: '/rewarehousing/chold' }, // Manage Warehouse > Rewarehousing > Global Content Adjustments
  w_fix_item_location: { to: '/admin/fix-item-locations' }, // Administrator > Management > Fix Item Locations
  w_fm11: { to: '/rewarehousing/fm11' }, // Manage Warehouse > Rewarehousing > Issue Full Pallet Moves
  w_global_location_assignment: { to: '/rewarehousing/location-assignment' }, // Manage Warehouse > Rewarehousing > Global Location Assignment
  w_global_velocity_update: { to: '/rewarehousing/velocity' }, // Manage Warehouse > Rewarehousing > Velocity Update
  w_manual_stock_list: { to: '/restock/manual-stock-list' }, // Activities > Restocking > Manual Stock List
  w_material_cut: { to: '/material/cut' }, // Activities > Material Cutting > Material Cut
  w_move_pallet: { to: '/inventory/move-pallet' }, // Activities > Inventory Move > Move Pallet
  w_move_pieces_fr_assign: { to: '/inventory/move-pieces-fr-assign' }, // Activities > Inventory Move > Move Pieces from Assigned Location
  w_move_pieces_fr_carton: { to: '/inventory/move-pieces-fr-carton' }, // Activities > Inventory Move > Move Pieces from Carton
  w_move_pieces_fr_location: { to: '/inventory/move-pieces-fr-location' }, // Activities > Inventory Move > Move Pieces from Location
  w_move_pieces_fr_pallet: { to: '/inventory/move-pieces-fr-pallet' }, // Activities > Inventory Move > Move Pieces from Pallet
  w_mover_rf: { to: '/movers/rf', requiredModule: 'MANAGE-WAREHOUSE-MOVER' }, // Manage Warehouse > Mover Maintenance > RF Mover
  w_mover_ws: { to: '/movers/ws', requiredModule: 'MANAGE-WAREHOUSE-MOVER' }, // Manage Warehouse > Mover Maintenance > WS Mover
  w_production_reconcile: { to: '/production/reconcile' }, // Activities > Production > Production Reconciliation
  w_purge_owner: { to: '/admin/purge-owner' }, // Administrator > Purge Owner
  w_qc_more_inventory: { to: '/receipts/qc-more-inventory' }, // Activities > QC Processing > QC More Inventory
  w_qc_pal_ctn_release: { to: '/receipts/qc-pal-ctn-release' }, // Activities > QC Processing > QC Pallet/Carton Release
  w_qc_quick_release: { to: '/receipts/qc-quick-release' }, // Activities > QC Processing > QC Quick Release
  w_quickship: { to: '/orders/quickship' }, // Activities > Order Processing > Orders and Waving > Quick Pick and Ship
  w_rep_man: { to: '/work/convert-manual-to-paper' }, // Manage Warehouse > Manage Work > Convert Manual Work from RF to Paper
  w_rep_opick: { to: '/orders/generate-load-sheets' }, // Activities > Order Processing > Trucks and Shipping > Generate Delivery/Load Sheets
  w_rep_pa: { to: '/receipts/generate-putaway-sheets' }, // Activities > Receipt Processing > Generate Putaway Sheets
  w_rep_rstk: { to: '/restocking/generate-restock-sheets' }, // Activities > Restocking > Generate Restock Sheets
  w_report_print: { to: '/receipts/fast-track-putaway-request', requiredModule: 'ACTIVITIES-RECEIPT-WORKER' }, // Activities > Receipt Processing > Request Fast Track Putaway Slip
  w_work_grab_status: { to: '/work/grab-status' }, // Manage Warehouse > Manage Work > Work Grab Status




  // ── salvaged promoted entries ──────────────────────────────────────────
  // 1 entry/entries were promoted inline (no longer /pb/...)
  // while still inside the auto-generated section. Moved here on the most
  // recent `build-placeholder-routes.mjs` run so the regen does not clobber
  // them. Safe to leave or relocate by hand.
  w_rstk_request: { to: '/browse/rstk-requests' }, // slice-202 list-only port

  // ── auto-generated placeholder routes ─────────────────────────────────
  // 40 PB windows that don't have real PH10 screens yet — each routes
  // to the generic /pb/:window placeholder. Generated by
  // `scripts/build-placeholder-routes.mjs`; do NOT edit by hand — re-run the script.
  // Excludes m_file (Owner) and m_reports per user direction (slice-37).
  w_ancient: { to: '/admin/archive-ancient' }, // slice-253: Administrator > Archive to Ancient History
  w_contents_maint: { to: '/inventory/contents-maint' }, // slice-242: Activities > Receipt Processing > Adjust Receipt
  // Slice-427: PB w_cyc_master is a maintenance/workflow window with 8 toolbar
  // actions, not a search/browse. New page at /cyc/master surfaces the cyc
  // list + selected-cyc options + the 8 PB actions. The legacy
  // /browse/cyc-master route is removed; this is where slice-251 should
  // have landed if the PB shape had been audited at scaffold time.
  w_cyc_master: { to: '/cyc/master' },
  w_generic_rep: { to: '/pb/w_generic_rep', placeholder: true }, // Configuration > Custom Report Configuration > Create Custom Report
  w_globin: { to: '/inventory/global-location-maint' }, // slice-252: Configuration > Location > Global Location Maintenance
  w_if_maint: { to: '/pb/w_if_maint', placeholder: true }, // Interfaces > Base System > Trigger Table  Maintenance
  w_import_data: { to: '/admin/imports/data' }, // slice-3427 (ADR-0043): Administrator > Import Data — web-native import wizard
  // slice-3490 (ADR-0043, Chris 08-26 for Russ): Import Inventory and Import Min/Max are
  // now ALSO importers on the web-native wizard, reached from Administrator > Import Data
  // and preselectable with /admin/imports/data?importer=inventory|min-max. These two leaves
  // deliberately still point at the PB-faithful screens: repointing them makes their
  // testers-hub catalog rows menu-unreachable (navCatalogReachability), and Chris ruled
  // 2026-08-05 that KNOWN_UNREACHABLE stays empty — "the testing should only point to
  // navigation reachable screens". Collapsing all three rows onto /admin/imports/data would
  // also fuse three separate issue histories into one.
  w_import_inventory: { to: '/admin/imports/inventory' }, // slice-245: Administrator > Import Inventory
  w_import_minmax: { to: '/admin/imports/minmax' }, // slice-244: Administrator > Import Min/Max
  w_import_sub_po_lines: { to: '/browse/import-sub-po-lines' }, // slice-3138: Administrator > Import Sub PO Lines (workbench shadows the generic browse cfg)
  w_item_apparel_itembuild: { to: '/config/apparel-itembuild', requiredModule: 'CONFIGURATION-ITEM' }, // slice-254: Configuration > Item > Apparel > Item Build
  w_kit_building_multi_ser_lot: { to: '/labels/kit-building-multi-ser-lot' }, // slice-249: Activities > Kit > Kit Building Multi-Serial Lot Component
  w_kit_building_serial: { to: '/labels/kit-building-serial' }, // slice-249: Activities > Kit > Kit Building Serialized Component
  w_label_central: { to: '/labels/central' }, // slice-249: Activities > Print Labels > Label Center
  w_label_print_std: { to: '/labels/print-std' }, // slice-249: Activities > Print Labels. The plain link opens LICENSE mode (Print License Labels); RePrint is m_reprintlicenselabel ?mode=license_rptr (slice-4495, Q40)
  w_material_cut_fast_pick: { to: '/activities/mat-cut-fast-pick' }, // slice-256: Activities > Material Cutting > Material Cut / Fast Pick
  w_material_cut_remain_fast_pick: { to: '/activities/mat-cut-remain-fast-pick' }, // slice-256: Activities > Material Cutting > Material Remain Fast Pick
  w_move_inventory_search: { to: '/browse/inventory-search' }, // slice-251: Activities > Inventory Move > Move Inventory Search
  w_order_allocation: { to: '/pb/w_order_allocation', placeholder: true }, // Activities > Order Processing > Order Adjustments > Order Allocation
  // slice-1619: w_order_split IS a standalone PB window (empty-packet open presets
  // warehouse/owner and the operator picks Order/Release from dddws). Dedicated screen.
  w_order_split: { to: '/orders/split', requiredModule: 'ACTIVITIES-OP-ORDERS-ADMIN' }, // Activities > Order Processing > Orders and Waving > Order Split
  // d12 (help gap g-orders-status-dashboard-001): dbxx.window_modules.default_module_id for
  // w_order_status_dashboard is ACTIVITIES-OP-ORDERS-WAVING (Chris_900v2, 2026-10-11), the same id
  // OrderStatusDashboardController gates on — it was carrying the Orders ADMIN neighbour's id.
  w_order_status_dashboard: { to: '/orders/status-dashboard', requiredModule: 'ACTIVITIES-OP-ORDERS-WAVING' }, // Activities > Order Processing > Orders and Waving > Order Status Dashboard
  w_pa_sort_by_item: { to: '/putaway-sort/by-item' }, // slice-236: Activities > Receipt Processing > Putaway Sort by Item
  w_pa_sort_by_zone: { to: '/putaway-sort/by-zone', requiredModule: 'ACTIVITIES-RECEIPT-WORKER' }, // slice-237: Activities > Receipt Processing > Putaway Sort by Zone
  w_pre_report: { to: '/pb/w_pre_report', placeholder: true }, // Activities > Cycle Counting > Cycle Manual Item Review Sheet
  // slice-286: m_reports tree had 3 other windows without WINDOW_TO_ROUTE entries; add
  // them as placeholders so they render clickable (instead of dimmed-non-clickable).
  // The disambiguation by ?id=<menu_id> in PbPlaceholderPage means each of the m_reports
  // items shows its own label/path even though many share w_pre_report.
  // slice-306: w_ai_report_assistant -> /reports/ask-me (POC1's Ask Me Reporting ported with
  // SELECT-only gate + row cap + timeout layered on top of the bare NL-to-SQL implementation).
  w_ai_report_assistant: { to: '/reports/ask-me', requiredModule: 'REPORTS' },
  // slice-3886 (ADR-0064): the Report Builder — synthetic window, injected by withReportBuilder().
  ph10_report_builder: { to: '/reports/builder', requiredModule: 'REPORTS' },
  w_genrpt_custom_rep: { to: '/pb/w_genrpt_custom_rep', placeholder: true }, // Reports > Run Custom Report (designer family, user-excluded)
  w_rep_bill_trans: { to: '/pb/w_rep_bill_trans', placeholder: true }, // Reports > Billing > Bill Transaction Report (eligible for future port)
  w_production_bb: { to: '/production/big-board', requiredModule: 'ACTIVITIES-PRODUCTION', hiddenInPb: true }, // PB ships m_productionbigboard visible=false (never un-hidden) — mirrored per Chris 2026-07-13
  // Decision Q89 (15 Oct): hidden until Purge is rebuilt on PB 9.0's purge_misc path; the
  // backend refuses every /api/admin/purge call (403) unless ph10.purge.enabled=true.
  w_purge: { to: '/admin/purge', devDisabled: true }, // Administrator > Purge History
  w_quick_label_load: { to: '/config/quick-label-load' }, // slice-255: Configuration > Labels/Reports > Labels > Quick Label  Load
  w_quickpick: { to: '/orders/quick-pick' }, // slice-4913 (was a /pb placeholder) — Activities > Order Processing > Orders and Waving > Quick Pick Confirmation
  w_rcv_pa_sort_by_zone: { to: '/putaway-sort/rcv-by-zone' }, // slice-238: Activities > Receipt Processing > Receiving Putaway Sort by Zone
  w_receive_product: { to: '/receipts/receive-product' }, // slice-243: Activities > Receipt Processing > Receive Product
  w_rep_bol: { to: '/reports/bol' }, // slice-246: Activities > Order Processing > Orders and Waving > Pick/Ship Reports
  // Slice-420 (BIG): PB w_rep_cyc full workflow port lives at /cyc/generate
  // (criteria + Generate List + tag/untag + Create Count + Profile save/load).
  // The legacy /reports/cyc summary-report page still exists at that URL.
  w_rep_cyc: { to: '/cyc/generate' },
  w_rep_prod_in_out: { to: '/reports/prod-in-out' }, // slice-278: Reports > Production > Actual vs Estimated Component Usage
  w_rep_wave: { to: '/reports/wave' }, // slice-247: Activities > Order Processing > Orders and Waving > Automatic Waving
  w_rtn_process_order: { to: '/receipts/returns-order' }, // slice-232: Activities > Receipt Processing > Order Return
  w_rtn_process_pickbin: { to: '/receipts/returns-pickbin' }, // slice-239: Activities > Receipt Processing > Return Product to Pick Bin
  w_rtn_process_tote: { to: '/receipts/returns-tote' }, // slice-240: Activities > Receipt Processing > Return Process Tote
  w_rtn_product: { to: '/receipts/returns-product' }, // slice-241: Activities > Receipt Processing > Return Product
  w_truck_schedule_manager: { to: '/browse/truck-schedule', requiredModule: 'ACTIVITIES-YARDMANAGE-MENU' }, // slice-251: Activities > Yard Management > Truck Schedule Manager
  w_va_item_bulk_labeling: { to: '/labels/va-item-bulk-labeling', requiredModule: 'ACTIVITIES-VAPROCESSING' }, // slice-249: Activities > VA Processing > VA Item Bulk Labeling
  w_va_item_labeling: { to: '/labels/va-item-labeling', requiredModule: 'ACTIVITIES-VAPROCESSING' }, // slice-249: Activities > VA Processing > VA Item Labeling
  w_wave_fastpick_pallbuild: { to: '/activities/wave-fastpick-pallbuild' }, // Activities > Order Processing > Fast Pick > Wave FastPick Pallet Build (full port, decision #19)
  // ── end auto-generated placeholder routes ─────────────────────────────
};

/** PH10 routes with no direct PB analogue (or only a parent-branch analogue). Listed as a
 *  synthetic "PH10" group at the bottom of the sidebar so they stay reachable without
 *  disrupting the PB tree. */
export type ExtraNavItem = {
  to: string;
  label: string;
  requiredModule?: string;
  requiredLevel?: number;
  /** slice-2849: shown only to the Test Screens admins (chris/link/link1). */
  adminOnly?: boolean;
  /** slice-3609: a testers-hub / internal-tooling surface — hidden when the instance's
   *  {@code testersHubEnabled} feature flag is false (the customer DEMO box). */
  hubOnly?: boolean;
  /** slice-3930: shown only when the instance's {@code topScreensEnabled} flag is on (the
   *  TEMPORARY "My top screens" survey, ph10-test only). */
  topScreensOnly?: boolean;
};

/** slice-2849: the Test Screens admin allowlist (case-insensitive login id) — see the
 *  full testers hub; everyone else gets only the guided /test-screens queue. */
export const TEST_SCREENS_ADMINS: ReadonlySet<string> = new Set(['CHRIS', 'LINK', 'LINK1']);

export function isTestScreensAdmin(userId: string | null | undefined): boolean {
  return !!userId && TEST_SCREENS_ADMINS.has(userId.trim().toUpperCase());
}

export const EXTRA_NAV_ITEMS: ExtraNavItem[] = [
  // slice-3590 (CHRIS 2026-09-08): REMOVED from this PH10 group -- Reports (/reports, the
  // generic landing), Security (/config/security), Open Issues (/feedback/queue), Ping
  // (/system/ping), Report PDF Capture (/report-pdf-capture) and Hardware Test
  // (/hardware-test). The ROUTES all still exist (deep links, the Testers Hub, and the
  // m_serialporttest alias to /hardware-test keep working); only the sidebar entries went.
  // slice-4210 (Chris 2026-10-14): the two slice-206/223 PH10 admin screens left this group —
  // Printer Configuration (/admin/printers, the ph10_printer_ext delivery-mode editor) is
  // REMOVED (calc-print owns delivery since ADR-0059; the table had no rows), and Print Log
  // moved into the PB menu under Configuration › Labels/Reports › Printers with that
  // menu's module (see PH10_PRINT_LOG_ITEM / withPh10PrintLog).
  // slice-778: Testers Hub -- UAT sign-off + issue tracking for every landable
  // PH10 page. Lives at the very bottom of the nav so testers always know where
  // to find their backlog. Ungated -- the hub is meta-tooling open to any
  // authenticated user (admins use the toggle inside to audit signed-off pages).
  // slice-2849 (CHRIS): the full hub is now admin-only (chris/link/link1); other testers
  // get the guided Test Screens queue below. The route also redirects non-admins.
  { to: '/testers-hub', label: 'Testers Hub', adminOnly: true, hubOnly: true },
  // slice-3659: Chris's review + coverage page for the functional round (flags from the test cards).
  { to: '/testers-hub/functional', label: 'Functional Round', adminOnly: true, hubOnly: true },
  // slice-3549 (CHRIS): index of the hidden v2 experiment pages (blank-slate reimaginings
  // per /v2_wins.html). Sits directly below Testers Hub per Chris's ask; the v2 pages
  // themselves are deliberately NOT in the nav — this index is their only discovery surface.
  { to: '/v2-screens', label: 'v2 Screens', hubOnly: true },
  // slice-2849: the guided one-screen-at-a-time review queue — every authenticated user.
  { to: '/test-screens', label: 'Test Screens', hubOnly: true },
  // slice-3683 (CHRIS 2026-09-17): the FUNCTIONAL round's own guided queue — "serves them the
  // page they should be working on, not a they get to pick from menu option". Deliberately NOT
  // adminOnly: this is the link most testers live in. The PB-fidelity "Test Screens" queue above
  // stays exactly as it is; the two hold independent leases.
  { to: '/functional-test-screens', label: 'Functional Test Screens', hubOnly: true },
  // slice-3930 (John 2026-10-06): TEMPORARY tester survey — pick the 25 screens needed to run the
  // warehouse day to day. Directly below Functional Test Screens; backend-flag gated
  // (ph10.topscreens.enabled, ph10-test only). Remove with the feature.
  { to: '/my-top-screens', label: 'My top screens', hubOnly: true, topScreensOnly: true },
  // slice-918 (CHRIS): rolling newest-first log of work Claude has shipped.
  // Sits directly below Testers Hub; backed by the static /whats-happening.html.
  { to: '/whats-happening', label: "What's Happening", hubOnly: true },
];

/**
 * Per-menu-id overrides for the rare case where two menu items point at the same PB
 * window but PH10 has split them into separate routes (e.g. CFG vs ADMIN modes of
 * {@code w_vendor_qc_update_rule}). Checked BEFORE {@link WINDOW_TO_ROUTE} so the more
 * specific id wins.
 */
export const ID_TO_ROUTE: Record<string, RouteEntry> = {
  // slice-172: ADMIN mode of w_vendor_qc_update_rule — the "Update Vendor QC %" action.
  // The same PB window opened with gstr_winparm.string1='ADMIN' instead of 'CFG'.
  // No nav gate by convention — the BE controller has @RequiresModule("ADMIN") which
  // rejects with 403 if the user lacks permission. Matches the sibling CFG entry
  // (w_vendor_qc_update_rule) which is also ungated in nav.
  m_vendorqcupdate: { to: '/vendor-qc/update', requiredModule: 'MANAGE-WAREHOUSE-VENDOR-QC' },

  // slice-3011: w_productivity_config's four PB menu leaves each launch ONE fixed mode
  // (m_main.srm clicked events set gstr_winparm.string1 + window_name
  // "w_productivity_config (<mode>)"): m_departmentmaintenance L41319 'dept' (tag
  // Configuration.Productivity.DepartmentMaintenance), m_shiftmaintenance L41357 'shift'
  // (…ShiftMaintenance), m_jobmaintenance L41395 'job' (…JobMaintenance), and
  // m_equipment L37026 'equip' — which lives under Configuration › PROCESS (tag
  // Configuration.Process.Equipment), not Productivity. Each mode is its own PH10
  // screen (titles = wf_title Department/Shift/Job/Equipment); without these overrides
  // all four leaves fell through to WINDOW_TO_ROUTE's single /browse/cfg-department
  // (the invented tabbed umbrella, removed this slice).
  m_departmentmaintenance: { to: '/browse/cfg-department', requiredModule: 'CONFIGURATION-PRODUCTIVITY' },
  m_shiftmaintenance: { to: '/browse/cfg-shift', requiredModule: 'CONFIGURATION-PRODUCTIVITY' },
  m_jobmaintenance: { to: '/browse/cfg-job', requiredModule: 'CONFIGURATION-PRODUCTIVITY' },
  m_equipment: { to: '/browse/cfg-equipment', requiredModule: 'CONFIGURATION-PROCESS' },
  // slice-173: INSERT mode of w_order_maint — "New Order" create form. The same PB
  // window opened with gstr_winparm.boolean1='Y'. The sibling m_ordermaintenance
  // (Order Search) routes to /orders/search via WINDOW_TO_ROUTE.
  m_neworder: { to: '/orders/new' },

  // slice-1869 (#1603, ROGER): INSERT mode of w_item_master — the "New Item" menu
  // (m_newitem) opens w_item_master with gstr_winparm.window_name="w_item_master (insert)"
  // + boolean1="Y" (pb-menu-tree.json m_newitem.actionRaw) — a blank ADD form, NOT the
  // maintenance/search window. Without this override m_newitem fell through to
  // WINDOW_TO_ROUTE's w_item_master → /browse/item-master (the search grid, "Item Master
  // Maintenance"), which is what the tester saw. Route it to the ItemMasterPage INSERT
  // mode at /items/master (no params) — the full dw_item_master_main create form
  // (slice-800), mirroring the sibling m_neworder → /orders/new. This is the same target
  // the browse toolbar's "New Item" action already drills to (browseConfigs items-master).
  m_newitem: { to: '/items/master' },
  // Slice-333 (feedback #15 ROGER): "Receive Product Auto" + "Receive Product Production"
  // open the same w_receive_product window in PB but with gstr_winparm.string8="Y" (auto)
  // or string7="Y" (prod). PH10 routes them to the same page with a ?mode= query param;
  // ReceiveProductPage reads it and adjusts title + banner accordingly. Per pb-menu-tree.json
  // L8502 (m_receiveproductauto) and the production sibling.
  m_receiveproductauto: { to: '/receipts/receive-product?mode=auto' },
  m_productionreceiving: { to: '/receipts/receive-product?mode=prod' },

  // slice-1152 (Chris): w_carton_repack backs 3 menu items via gstr_winparm.string1
  // ('repack' / 'pick pack' / 'pick pack check' — m_main.srm L10195/L10236/L10277).
  // Mode is fixed by the launching menu item, NOT an in-window picker. m_cartonrepack
  // → /cartons/repack (REPACK) via WINDOW_TO_ROUTE; override the other two so each
  // lands on its own route + fixed mode + PB-verbatim title (no shared deep-link).
  m_cartonpickandpack: { to: '/cartons/pickpack', requiredModule: 'ACTIVITIES-OP-CARTONS' },
  m_cartonpickandpackwithcheck: { to: '/cartons/pickpack-check', requiredModule: 'ACTIVITIES-OP-CARTONS' },

  // slice-2921 (conformance-238): "Post Deduct" and "Post Deduct History" both open
  // w_postdeduct in PB — m_postdeducthistory passes gstr_winparm.string1='hist'
  // (w_postdeduct.srw:63-71: hist retitles, swaps to the dw_postduct_hist_* DWs and
  // disables save/insert/delete, adding Purge History Records). Without this override
  // the menu id fell through to WINDOW_TO_ROUTE's w_postdeduct → /browse/post-deduct-01
  // (the ACTIVE editor) and the hist route was unreachable from the sidebar.
  m_postdeducthistory: { to: '/browse/post-deduct-hist-01' },

  // FB#82 (DENISE): "Return Product" and "Return Product By Item" both open
  // w_rtn_product in PB, the latter with gstr_winparm.string1="BYITEM". Without
  // an override, both menu items mapped to the same /receipts/returns-product
  // route and the highlighter promoted whichever was first in the tree -- so
  // selecting "Return Product" lit up "Return Product By Item". Route the by-item
  // variant to ?mode=byitem so the highlighter picks the unique URL match.
  m_returnproductbyitem: { to: '/receipts/returns-product?mode=byitem' },

  // Slice-483 (FB#244/#248 replacement): "Global Order Adjustment" and
  // "Global Order Line Adjustment By Item" both open w_global_order_maint
  // in PB, the latter with gstr_winparm.boolean1="Y" + window_name suffix
  // "(detail)". Same window, two operator modes per w_global_order_maint.srw
  // lines 53-75: header mode (estimate / group-modify / delete / cancel /
  // ship-info / fast-pick) vs by-item line mode (group-line add/change/
  // delete/cancel). Without this override both menu items routed to
  // /browse/global-order-maint and lit up together. Route the by-item
  // variant to its own browse cfg key so the highlighter and toolbar
  // pick the right mode.
  m_globalorderlineadjustmentbyitem: { to: '/browse/global-order-maint-byitem', requiredModule: 'ACTIVITIES-OP-ORDER-ADJUST' },

  // slice-3013 (§4.23, supersedes slice-1620's ?mode= fold): "Multi-Carton Repack" and
  // "Multi-Carton Pick and Pack" are TWO PB menu leaves of w_carton_repack_1_m —
  // m_multi_cartonrepack passes gstr_winparm.string1='N' ("Do not allow to pick"),
  // m_multicartonpickandpack passes 'Y' ("allow to pick") — m_main.srm; the open event
  // maps string1 → pick_flag + verbatim wf_title (.srw L584-590). One route per leaf,
  // mode LOCKED at launch (Chris platform ruling 2026-07-21); the old folded
  // /browse/carton-repack-1-m route redirects and RouteNormalizer aliases it.
  m_multi_cartonrepack: { to: '/browse/multi-carton-repack', requiredModule: 'ACTIVITIES-OP-CARTONS' },
  m_multicartonpickandpack: { to: '/browse/multi-carton-pick-and-pack', requiredModule: 'ACTIVITIES-OP-CARTONS' },

  // slice-3020 (#2126, §4.23): w_configuration's TWO PB menu leaves, mode locked at launch
  // (gstr_winparm.string1). m_sysconfig (Configuration › System › "Configuration", tag
  // "Configuration.System.SysConfig") passes '(sys)' — m_main.srm:38043-38044 → verbatim
  // wf_title "Configuration (System)" (w_configuration.srw:352). m_usersetting_phfc
  // (Window › "&User Setting", ancestor m_phfc_main.srm; m_main.srm override :7455-7456)
  // passes '(usr)' → wf_title "Configuration (User)" (srw:350). The Window top-level menu
  // is DROPPED from the sidebar (build-nav.mjs), so the (usr) entry never renders as a nav
  // item — it documents the leaf and keeps deep-links/highlighting correct; the catalog row
  // is injected via generate-page-catalog EXTRA_ROUTES.
  m_sysconfig: { to: '/browse/configuration' },
  m_usersetting_phfc: { to: '/browse/configuration-user' },

  // FB#165 (DENISE): four menu items all open w_label_print_std with different
  // gstr_winparm.string1 values (LICENSE_RPTR / MISC / LICENSE / CONTENTS).
  // Without per-id overrides, the nav highlighter lit up all four whenever any
  // one was selected. Route each to its own ?mode= so the highlighter resolves
  // to a unique URL. LabelPrintStdPage doesn't yet branch on mode -- the route
  // uniqueness alone fixes the highlight; per-mode UI behavior is a follow-up.
  m_reprintlicenselabel: { to: '/labels/print-std?mode=license_rptr' },
  m_printmiscellaneouslabels: { to: '/labels/print-std?mode=misc' },
  m_printlicenselabels: { to: '/labels/print-std?mode=license' },
  m_printcontentlabels: { to: '/labels/print-std?mode=contents' },

  // FB#193 (DENISE): "Receipt Search" and "New Receipt" both opened
  // w_receipt_search / w_receipt_maint which slice-376 mapped to the same
  // /browse/receipt-maint route (until the Tier-2 maintenance form lands).
  // Same nav-highlight issue as FB#82/165: both menu items lit up together.
  // Route "New Receipt" to ?mode=new so the highlighter picks the unique URL;
  // slice-887 (FB#93, ROGER): New Receipt opens the receipt CREATE page (it
  // exists now), not the receipt-maint browse grid.
  m_newreceipt: { to: '/receipts/new' },

  // slice-2687 (no-approval batch-08 #10): PB opens w_phfc_wizard_header in two menu
  // contexts via gstr_winparm.string1 — m_wizards (Configuration > System > Wizards,
  // m_main.srm:38108) passes '(sys)' → "Wizards (System)" with runwizard enabled
  // (w_phfc_wizard_header.srw:26-31); m_wizards_phfc (Configuration > System Phfc,
  // m_phfc_main.srm:6729) opens it plain → the User context. WINDOW_TO_ROUTE keeps
  // w_phfc_wizard_header → /browse/phfc-wizard-header (User) for deep links; these
  // per-id overrides point each PB menu item at its own context. ADDITION only — no
  // route rename, so no ROUTE_ALIASES entry (testers-hub route-drift rule).
  // Wizards disabled 2026-07-16 (Bob) — never made it out of dev. devDisabled drops both PB
  // menu contexts from the sidebar; routes stay registered so no code is deleted.
  m_wizards: { to: '/browse/phfc-wizard-header-system', devDisabled: true },
  m_wizards_phfc: { to: '/browse/phfc-wizard-header', devDisabled: true },

  // ── slice-288: PB-reports runner (first batch of 10 SP-backed reports) ──
  // Each m_* under m_reports points at the generic /reports/pb/<key> page; the page
  // pulls the registry entry for <key> from docs/pb-reports-registry.json (shared
  // BE+FE source of truth). Adding more is a single line here once the registry
  // entry exists; the runner handles SP dispatch + criteria form + PDF render.
  m_agedinventory: { to: '/reports/pb/inv-aged' },
  m_availableinventory: { to: '/reports/pb/inv-available' },
  m_in_transit: { to: '/reports/pb/inv-intransit' },
  m_itemstatus: { to: '/reports/pb/inv-item-status' },
  m_shelfliferemaining1: { to: '/reports/pb/inv-shelf-life-remain' },
  m_dailytransactions: { to: '/reports/pb/invlot-daily-trans' },
  m_itemsshortfororderdemand: { to: '/reports/pb/items-short' },
  m_inventoryhold: { to: '/reports/pb/inventory-on-hold' },
  m_itemsinkit: { to: '/reports/pb/items-in-kit' },
  m_locationdetails: { to: '/reports/pb/loc-details' },

  // ── slice-301: batch 13 (final 24 inline-SQL reports -- 271/271 = 100%) ──
  m_itemdiscrepancysummary: { to: '/reports/pb/cyc-item-disc-summary' },
  m_itemdiscrepancydetail: { to: '/reports/pb/cyc-item-disc-detail' },
  m_locationdiscrepancysummary: { to: '/reports/pb/cyc-loc-disc-summary' },
  m_locationdiscrepancydetail: { to: '/reports/pb/cyc-loc-disc-detail' },
  m_suspendedreceipts: { to: '/reports/pb/receipts-suspended' },
  m_shippedweightbydate: { to: '/reports/pb/shipped-weight-by-date' },
  m_shippeditemsbylot: { to: '/reports/pb/shipped-items-by-lot' },
  m_orderturnover: { to: '/reports/pb/order-turnover' },
  m_employeecomparisonreport: { to: '/reports/productivity-workbench?mode=compare&dim=employee' }, // W1 decision
  m_jobcomparisonreport: { to: '/reports/productivity-workbench?mode=compare&dim=job' },
  m_departmentcomparisonreport: { to: '/reports/productivity-workbench?mode=compare&dim=department' },
  m_shiftcomparisonreport: { to: '/reports/productivity-workbench?mode=compare&dim=shift' },
  m_onhandcontainers: { to: '/reports/pb/containers-onhand' },
  m_shippedcontainers: { to: '/reports/pb/containers-shipped' },
  m_serializedkitbuilding: { to: '/reports/pb/serialized-kit-building' },
  m_itemunderstock: { to: '/reports/pb/items-understock' },
  m_itemoverstock: { to: '/reports/pb/items-overstock' },
  m_inventorybypallet: { to: '/reports/pb/inv-by-pallet-flat' },
  m_pickpackwithcheck: { to: '/reports/pb/pick-pack-with-check' },
  m_exceptionsreport: { to: '/reports/pb/work-exceptions' },
  m_warehouseusage: { to: '/reports/pb/whs-usage' },
  m_warehouseusagechart: { to: '/reports/pb/whs-usage-chart' },
  m_valueaddbypallet: { to: '/reports/pb/va-by-pallet' },
  m_valueaddbyitem: { to: '/reports/pb/va-by-item' },

  // ── slice-300: batch 12 (10 inline-SQL reports via runner v2 sqlTemplate mode) ──
  m_customerlist: { to: '/reports/pb/customer-list' },
  m_vendorlist: { to: '/reports/pb/vendor-list' },
  m_deletedorders1: { to: '/reports/pb/orders-deleted' },
  m_billingtransactiondetail: { to: '/reports/billing-workbench?mode=detail' }, // W3 decision
  m_billingtransactionsummary: { to: '/reports/billing-workbench?mode=summary' },
  m_billingtransactions: { to: '/reports/billing-workbench?mode=byuser' },
  m_shippedordersbydate: { to: '/reports/pb/shipped-orders-by-date' },
  m_ordertransactionhistory: { to: '/reports/pb/order-tran-hist' },
  m_transactionbydate: { to: '/reports/pb/transactions-by-date' },
  m_receivedvsposted: { to: '/reports/pb/received-vs-posted' },

  // ── slice-298: batch 11 (25 more reports from unused-SP scan -- 87% coverage) ──
  m_receipthistorybyitem: { to: '/reports/pb/receipt-hist-item' },
  m_partiallyshippedorders: { to: '/reports/pb/partial-ship-by-carrier' },
  m_valueshipped: { to: '/reports/pb/shipment-value' },
  m_shippedcartonsbyshipmethod: { to: '/reports/pb/rpt-shipped-cartons' },
  m_shipmentwithtrackinginformation: { to: '/reports/pb/shipped-order-track' },
  m_inventorybyreceipt: { to: '/reports/pb/inv-by-receipt' },
  m_packstationload: { to: '/reports/pb/pack-station-load' },
  m_orderassignmentactivitybydate: { to: '/reports/pb/order-activity-by-date' },
  m_openordertimeestimate: { to: '/reports/pb/order-time-estimate' },
  m_orderdeliverytime: { to: '/reports/pb/order-delivery-time' },
  m_suspendedcancelledorders: { to: '/reports/pb/orders-suspended' },
  m_reporderdeletedorders: { to: '/reports/pb/orders-deleted-rep' },
  m_waveallocationsummary: { to: '/reports/pb/wave-alloc-summary' },
  m_labordetail: { to: '/reports/pb/labor-detail' },
  m_actualvsestimatedcomponentusage: { to: '/reports/pb/prod-comp-usage' },
  m_productioncomponentusagebyrun: { to: '/reports/pb/prod-comp-usage-by-run' },
  m_transactionbyuserdate: { to: '/reports/pb/tran-by-user-date' },
  m_locationswithcycwork: { to: '/reports/pb/loc-in-cyc' },
  m_employeeproductivitydetail: { to: '/reports/productivity-workbench?mode=detail' }, // W2 decision
  m_employeeproductivitysummary: { to: '/reports/productivity-workbench?mode=summary' },
  m_locationtypeusage: { to: '/reports/pb/loctype-usage' },
  m_locationtypeusagechart: { to: '/reports/pb/loctype-usage-chart' },
  m_inventorybyitemclasslocationlot: { to: '/reports/pb/invlot-by-classloc-base' },
  m_lateorders: { to: '/reports/pb/orders-late' },
  m_picktostorestatusforwave: { to: '/reports/pb/puttostore-status' },

  // ── slice-297: batch 10 (25 fuzzy-discovered SPs -- broad cyc / billing / receipts coverage) ──
  m_lotrecallbom: { to: '/reports/pb/lot-recall-bom' },
  m_qcreport: { to: '/reports/pb/qc-report' },
  m_palletstoragesnapshot: { to: '/reports/pb/bill-pallet-storage' },
  m_billingshippedvolume: { to: '/reports/pb/bill-shipped-volume-billing' },
  m_monthlystoragebyowner: { to: '/reports/pb/bill-monthly-storage' },
  m_billingreceivedvolume: { to: '/reports/pb/bill-received-volume' },
  m_itemaccuracydetail: { to: '/reports/pb/cyc-item-acc-det' },
  m_itemaccuracysummary: { to: '/reports/pb/cyc-item-acc-sum' },
  m_itemaccuracybycount: { to: '/reports/pb/cyc-item-acc-count' },
  m_itemvaluation: { to: '/reports/pb/cyc-item-valuation' },
  m_itemdetailbydate: { to: '/reports/pb/cyc-item-det-by-date' },
  m_itemsummary: { to: '/reports/pb/cyc-item-sum' },
  m_locationsummary: { to: '/reports/pb/cyc-loc-sum' },
  m_lostinventorybyitem: { to: '/reports/pb/cyc-lost-inv-by-item' },
  m_itemswithoutcubeorweight: { to: '/reports/pb/items-wo-cube-weight' },
  m_closedreceiptcartoncount: { to: '/reports/pb/closed-po-ctn-cnt' },
  m_expectedvsactualreceived: { to: '/reports/pb/receipt-exp-vs-act' },
  m_actuallotsreceived: { to: '/reports/pb/received-items-by-lot' },
  m_docktostock: { to: '/reports/pb/receipt-dock-to-stock' },
  m_freightchargesbyshipmethod: { to: '/reports/pb/freight-charges' },
  m_shippeditemsbydate: { to: '/reports/pb/shipitems-by-date' },
  m_locationswithmultipleassignments: { to: '/reports/pb/loc-multi-assigned' },
  m_inventorypalletcountbyitemclass: { to: '/reports/pb/inv-pal-cnt-by-class' },
  m_inventorybalancedwithlot: { to: '/reports/pb/invlot-balanced' },
  m_bulkworkbyarea: { to: '/reports/pb/bulk-work-by-area' },

  // ── slice-296: batch 9 (12 SPs discovered via .srd procedure= scrape) ──
  m_billedboxcount: { to: '/reports/pb/bill-box-count' },
  m_billingtransactionsbydate: { to: '/reports/pb/bill-trans-all' },
  m_itemdetail: { to: '/reports/pb/cyc-item-detail' },
  m_receipttransactionhistory: { to: '/reports/pb/receipts-tran-hist' },
  m_vendorhistoryovershort: { to: '/reports/pb/vendor-hist-os' },
  m_itemsummarybyreceipttype: { to: '/reports/pb/receipts-itemsum-byrcpttp' },
  m_appointmentschedule_rpt: { to: '/reports/pb/rec-appointment' },
  m_shipmentaverages: { to: '/reports/pb/ship-averages' },
  m_transactionsummarybyitem: { to: '/reports/pb/tran-sum-by-item' },
  m_inventoryinoutwithlot: { to: '/reports/pb/invlot-in-out' },
  m_stagedorders: { to: '/reports/pb/ord-staged' },
  m_prodstatus: { to: '/reports/pb/prod-status' },

  // ── slice-295: batch 8 (25 -- last SP-backed batch; mix of dw_rep_ + CycRprt_ + rpt_ + report_ SPs) ──
  m_countedlocationsbydate: { to: '/reports/pb/cyc-counted-loc-by-date' },
  m_notcountedlocationsbydate: { to: '/reports/pb/cyc-not-counted-by-date' },
  m_notcountedlocations: { to: '/reports/pb/cyc-not-counted-loc-base' },
  m_openreceiptsbyitem: { to: '/reports/pb/receipts-open-by-item-base' },
  m_shippeditemsbyholdcode: { to: '/reports/pb/shipped-items-by-hold' },
  m_32: { to: '/reports/pb/order-process-times' },
  m_shippedordersdailysummary: { to: '/reports/pb/shipped-orders-daily' },
  m_shippedorders1: { to: '/reports/pb/shipped-orders' },
  m_worktasksbylocationtype: { to: '/reports/pb/work-on-truck-loctype' },
  m_worktimesummarychart: { to: '/reports/pb/work-on-truck-time' },
  m_unassigneditemsonorders: { to: '/reports/pb/items-unassigned-ord' },
  m_locationdetail: { to: '/reports/pb/cyc-loc-detail' },
  m_manuallocationreview1: { to: '/reports/pb/rpt-manual-loc-review' },
  m_lostinventorybydate: { to: '/reports/pb/cyc-lost-inv-by-date' },
  m_lostinventorybycycid: { to: '/reports/pb/cyc-lost-inv-by-cycid' },
  m_manualitemreview: { to: '/reports/pb/rpt-manual-item-review' },
  m_pallets: { to: '/reports/pb/loc-count-pallets' },
  m_cartons: { to: '/reports/pb/loc-count-cartons' },
  m_laborstandardbyuser: { to: '/reports/pb/rpt-labor-standard' },
  m_restocksbylocation: { to: '/reports/pb/inv-rstk-by-location' },
  m_shipmentanalysis: { to: '/reports/pb/rpt-wave-estimate' },
  m_ownerlist: { to: '/reports/pb/purge-owner-list' },
  m_shipmentanalysistruck: { to: '/reports/pb/rpt-truck-estimate' },
  m_percentcompletebywave: { to: '/reports/pb/rpt-pct-complete-by-wave' },
  m_orderassignmentactivitybyuser: { to: '/reports/pb/user-order-activity' },

  // ── slice-294: batch 7 (25 same-SP variants -- distinct routes, shared backing SP) ──
  m_inventorybalancedwithinventorytype: { to: '/reports/pb/inv-balanced-invtype' },
  m_inventoryonhold: { to: '/reports/pb/inv-onhold-base' },
  m_inventoryvaluation1: { to: '/reports/pb/inv-valuation-base' },
  m_inventorybyitemclasslocation: { to: '/reports/pb/inv-by-class-loc' },
  m_inventorybystylecolorlocation: { to: '/reports/pb/inv-by-stylecolor-loc' },
  m_inventoryinbom: { to: '/reports/pb/inv-in-bom-base' },
  m_dailytransactionslot: { to: '/reports/pb/invlot-daily-trans-base' },
  m_inventorybylocationlot: { to: '/reports/pb/invlot-by-loc-lot' },
  m_invlot_lotexpiration: { to: '/reports/pb/invlot-lotexp-base' },
  m_itemvelocity: { to: '/reports/pb/item-velocity' },
  m_itemswithoutcfgcode: { to: '/reports/pb/items-wo-cfg-base' },
  m_unassigneditemswithinventorylocation: { to: '/reports/pb/items-unassigned-inv' },
  m_blockedlocationswithinventory: { to: '/reports/pb/loc-blocked-with-inv' },
  m_locationrulesviolation: { to: '/reports/pb/loc-rules-violation' },
  m_locationslocations: { to: '/reports/pb/loc-locations-base' },
  m_mismatchedassignlocation: { to: '/reports/pb/loc-mismatch-assign' },
  m_itemstoshortship: { to: '/reports/pb/items-to-short-ship' },
  m_negativeavailablebylocation0: { to: '/reports/pb/locations-short-alt' },
  m_shortshippeditems: { to: '/reports/pb/short-shipped-items' },
  m_openordersbyship_bydate: { to: '/reports/pb/open-orders-shipby-date' },
  m_openpickswavezone: { to: '/reports/pb/open-picks-wave-zone' },
  m_openpicksforwavebyzoneandordertype: { to: '/reports/pb/open-picks-wave-z-ot' },
  m_employeeproductivitygrid: { to: '/reports/pb/empl-prod-grid-base' },
  m_vendorqcpercentageupdatehistory: { to: '/reports/pb/vendor-qc-perf-hist' },
  m_billingsummary: { to: '/reports/pb/billing-summary-closed' },

  // ── slice-293: batch 6 (25 more SP-backed reports -- 14 fresh + 11 same-SP variants) ──
  m_shortitemsondroppedorder: { to: '/reports/pb/ord-items-reject' },
  m_employeeperformancesummarybytask: { to: '/reports/pb/prod-perf03s' },
  m_receiptswithcf: { to: '/reports/pb/receipts-with-cf' },
  m_shipmentanalysis1: { to: '/reports/pb/shipment-analysis' },
  m_shipmentwithpalletcartoninformation: { to: '/reports/pb/shipments-by-containers' },
  m_shipmentsbycarrierwithitemdetail: { to: '/reports/pb/shipments-by-carrier-det' },
  m_shipmentsbycustomer: { to: '/reports/pb/shipments-by-customer' },
  m_shipmentsbypallettype: { to: '/reports/pb/shipments-by-pallet' },
  m_shipmentsbyshipto: { to: '/reports/pb/shipments-by-shipto' },
  m_shippedcartonsbystore: { to: '/reports/pb/cartons-by-bol' },
  m_shippeddunnage: { to: '/reports/pb/shipped-dunnage' },
  m_shippedorderdetails: { to: '/reports/pb/shiporddetails' },
  m_shippedvolumebydate: { to: '/reports/pb/bill-shipped-volume' },
  m_shippingmanifestbycarrier: { to: '/reports/pb/shipman-by-carrier' },
  m_inventorybalancedbydate: { to: '/reports/pb/inv-balanced-by-date' },
  m_inventoryhistory: { to: '/reports/pb/inv-history' },
  m_inventoryinout: { to: '/reports/pb/inv-inout' },
  m_inventoryonhand: { to: '/reports/pb/inv-onhand-base' },
  m_inventorybyitem1: { to: '/reports/pb/inv-by-item-base' },
  m_inventorybyclass: { to: '/reports/pb/inv-by-class' },
  m_inventorybylocation: { to: '/reports/pb/inv-by-location' },
  m_inventorybylocationtype: { to: '/reports/pb/inv-by-loctype' },
  m_assignedlocations: { to: '/reports/pb/loc-assigned' },
  m_blockedlocations: { to: '/reports/pb/loc-blocked' },
  m_notcountedlocations1: { to: '/reports/pb/loc-not-counted' },

  // ── slice-292: batch 5 (20 more SP-backed reports) ──
  m_inventorybylocationandinventorytype: { to: '/reports/pb/inv-locinvtype' },
  m_itemlocationhistory: { to: '/reports/pb/item-location-history' },
  m_locationconsolidation: { to: '/reports/pb/loc-consolidation' },
  m_inventoryinboms: { to: '/reports/pb/inventory-in-boms' },
  m_inventorybyitemlot: { to: '/reports/pb/invlot-by-classloc' },
  m_outofstockitems: { to: '/reports/pb/inv-in-out' },
  m_openwavesummarybyitem: { to: '/reports/pb/wave-summary2' },
  m_ordersorderstatus: { to: '/reports/pb/order-status' },
  m_percentcompletebyorder: { to: '/reports/pb/order-percentage' },
  m_preroutingestimationbyuniformcartons: { to: '/reports/pb/pre-routing-est' },
  m_rejectedorders: { to: '/reports/pb/rejected-orders' },
  m_employeeperformancesummarybyqty: { to: '/reports/pb/prod-perf02s' },
  m_employeeperformancebytask: { to: '/reports/pb/prod-perf03' },
  m_valueaddrfidlabels: { to: '/reports/pb/rfid-labels' },
  m_receiptcommments: { to: '/reports/pb/receipt-comments' },
  m_receiptdetails: { to: '/reports/pb/receipt-details' },
  m_receiptimages: { to: '/reports/pb/receipt-images' },
  m_receiptputawaybypallet: { to: '/reports/pb/receipt-putaway' },
  m_receiptsbytypeanddate: { to: '/reports/pb/receipts-by-type-date' },
  m_receivingticket: { to: '/reports/pb/receiving-ticket' },

  // ── slice-291: batch 4 (25 more SP-backed reports) ──
  m_worktasksummary: { to: '/reports/pb/work-task-sum' },
  m_openorderworktaskestimate: { to: '/reports/pb/open-order-task-est' },
  m_cartontypeusagebywave: { to: '/reports/pb/wave-carton-type' },
  m_locationusagebyowner: { to: '/reports/pb/usage-loctype' },
  m_worktasksummarychart: { to: '/reports/pb/work-on-truck' },
  m_negativeavailablebylocation: { to: '/reports/pb/locations-short' },
  m_openpicksforwave: { to: '/reports/pb/open-picks-for-wave' },
  m_orderturnaroundbydate: { to: '/reports/pb/order-turnaround' },
  m_inventorysummarywithbom: { to: '/reports/pb/invbom-sum-w-bom' },
  m_orderpickstatus: { to: '/reports/pb/order-pick-status' },
  m_underutilizedlocationsbytype: { to: '/reports/pb/utilization-under' },
  m_inventoryonhandvspicked: { to: '/reports/pb/inv-onhand-vs-picked' },
  m_inventoryvaluationbypo: { to: '/reports/pb/inv-valuation-by-po' },
  m_inventorybystylecolor1: { to: '/reports/pb/inv-by-stylecolor' },
  m_inventorybybuilding1: { to: '/reports/pb/inv-by-building' },
  m_shipmentdetailbyorder: { to: '/reports/pb/shipdet-by-order' },
  m_shipmentsbycarrier: { to: '/reports/pb/shipments-by-carrier' },
  m_shippedlotsbydate: { to: '/reports/pb/shipped-lots-by-date' },
  m_inventoryonhandwithpicked: { to: '/reports/pb/inv-onhand-w-picked' },
  m_openordersbystatus: { to: '/reports/pb/orders-by-status' },
  m_shippedcontainersbytype: { to: '/reports/pb/ship-container-type' },
  m_shipmenttotalsbydate: { to: '/reports/pb/ship-tot-by-date' },
  m_inventorybalancedwithvolumeandweight: { to: '/reports/pb/inv-balanced-vol' },
  // PB delta 2026-07-07: net-new PB report (dw_rep_inv_balanced_std1/2) — the date-range
  // "activity analysis" variant of the balanced family, menu m_main.srm:20266-20282.
  m_inventoryactivityanalysis: { to: '/reports/pb/inv-activity-analysis' },
  m_picksagainstpending: { to: '/reports/pb/picks-from-pending' },
  m_wavemanagement: { to: '/reports/pb/wave-management' },

  // ── slice-290: batch 3 (25 more SP-backed reports) ──
  m_inventorybelowminmax: { to: '/reports/pb/inv-below-minmax' },
  m_inventoryincludingbom: { to: '/reports/pb/inv-in-bom' },
  m_grosscomponentrequirements: { to: '/reports/pb/inv-comp-req' },
  m_agedinventorylot: { to: '/reports/pb/invlot-aged' },
  m_inventoryonhandlot: { to: '/reports/pb/invlot-onhand' },
  m_locationsovercapacity: { to: '/reports/pb/loc-over-capacity' },
  m_unassignedlocations: { to: '/reports/pb/loc-unassigned' },
  m_recentshippeditems: { to: '/reports/pb/inv-recent-shipped' },
  m_negativeavailable: { to: '/reports/pb/inv-negative-available' },
  m_shortitemsonwave: { to: '/reports/pb/inv-short-on-wave' },
  m_pickedinventorybylocation: { to: '/reports/pb/inv-picked-by-loc' },
  m_openordersbyordertype: { to: '/reports/pb/shipby-orders' },
  m_orderkitbuild: { to: '/reports/pb/order-kit-build' },
  m_ordercomments: { to: '/reports/pb/order-comments' },
  m_employeeperformancebyqty: { to: '/reports/pb/prod-perf02' },
  m_employeeproductivityperformance: { to: '/reports/pb/prod-perf01' },
  m_employeelogin: { to: '/reports/pb/empl-prod-grid' },
  m_openreceiptsbytruck: { to: '/reports/pb/receipts-open-by-truck' },
  m_openreceiptsbytype: { to: '/reports/pb/receipts-open-by-type' },
  m_openreceipts: { to: '/reports/pb/receipts-open-by-item' },
  m_shippedlotsbydivision: { to: '/reports/pb/shipped-lots-by-division' },
  m_shipmentsbydivision: { to: '/reports/pb/shipments-by-division' },
  m_orderedvsshipped: { to: '/reports/pb/ordered-vs-shipped' },
  m_usertransaction: { to: '/reports/pb/user-transaction' },
  m_minmax: { to: '/reports/pb/utilization-minmax' },

  // ── slice-289: batch 2 (20 more SP-backed reports) ──
  m_lotexpiration: { to: '/reports/pb/lot-expiration' },
  m_lotrecall: { to: '/reports/pb/lot-recall' },
  m_lotrecallshipped: { to: '/reports/pb/lot-recall-shipped' },
  m_vendorqcprcupdatechange: { to: '/reports/pb/vendor-qc-prc-upd-chg' },
  m_vendorqcperformance: { to: '/reports/pb/vendor-qc-performance' },
  m_billinginvoicesummary: { to: '/reports/pb/billing-invoice-summary' },
  m_billingtransactiondetailsbydate: { to: '/reports/pb/bill-trans-by-date' },
  m_countedlocations: { to: '/reports/pb/cyc-counted-locations' },
  m_assigneditems: { to: '/reports/pb/items-assigned' },
  m_flaggeditembyitemclass: { to: '/reports/pb/items-flagged' },
  m_itemconfiguration: { to: '/reports/pb/item-configuration' },
  m_itemdefaultcfginvcode: { to: '/reports/pb/items-without-cfg' },
  m_itemlocationvelocity: { to: '/reports/pb/items-location-velocity' },
  m_itemsaveragebytransaction: { to: '/reports/pb/items-pick-avg' },
  m_itemsbyitemclass: { to: '/reports/pb/inv-by-itemclass' },
  m_itemsbyvendor: { to: '/reports/pb/inv-by-item' },
  m_itemsinmultiplelocations: { to: '/reports/pb/items-multiple-loctype' },
  m_itemswithbom: { to: '/reports/pb/items-in-bom' },
  m_unassigneditems: { to: '/reports/pb/items-unassigned' },
  m_crossdockdocument: { to: '/reports/pb/rec-cross-dock' },

  // ── slice-3454: eight ALREADY-BUILT screens no nav leaf pointed at ────────────────
  // From the 2026-08-25 nav-graybar review (rows N7/N8, N15-N20). Each of these screens is
  // live in PH10 today; the menu leaf that opens it in PB resolved to nothing because its
  // clicked event lives in the m_phfc_main ancestor (so the parse recorded no window) or
  // because the window it names is the PHFC twin of the one PH10 ported. Keying the ROUTE by
  // menu id is the whole fix — no new WINDOW_TO_ROUTE entries, so the tester catalog (which
  // walks windows) is untouched.
  //
  // N7/N8: the Activities > Cycle Counting twins of two Reports leaves. Same PB report
  // (w_pre_report string2=manual_location_review / manual_item_review), same PH10 route as
  // m_manuallocationreview1 / m_manualitemreview above.
  m_manuallocationreview: { to: '/reports/pb/rpt-manual-loc-review' },
  m_manualitemreviewsheet: { to: '/reports/pb/rpt-manual-item-review' },
  // N20 Report Master (PB "Report &Master") → w_report_master, slice-280. NOTE: four actions
  // INSIDE that screen are still unbuilt (docs/audits/placeholder-actions-2026-08-24.md L3 —
  // cascaded delete, report-confirm, submit-for-reprint, stored-report viewer); wiring the
  // leaf exposes them, so L3 is the paired follow-up.
  // slice-3599 (card 138/#3, dev-rules §4.23 split): was the SAME route as m_reportsearch
  // below — one page whose auto-retrieve-vs-wait-for-Search behaviour depended on which
  // menu you clicked, the exact multi-mode confusion §4.23 exists to stop. Each leaf now
  // gets its own cfg key with the mode locked at launch (browseConfigs.ts 'report-master').
  m_reportmaster: { to: '/browse/report-master' },
  // N19 Report Search (PB "Report &Search") → w_report_search, slice-280.
  // slice-3599: split off '/browse/report-master' onto its own route/cfg
  // ('report-master-search') — PB waits for the operator to Search here (w_search /
  // w_query_base guard) where w_report_master auto-retrieves; see the cfg's comments.
  m_reportsearch: { to: '/browse/report-master-search' },
  // N16 Report Profiles (PB "Report Pro&files") → w_report_profiles, slice-281 (ADR-0022
  // launcher renders at /browse/profile-header).
  m_reportprofiles: { to: '/browse/profile-header' },
  // N15 Saved Report Search (PB "&Saved Report Search") → w_ws_saved_report_search, slice-283.
  m_wssavedreportsearch: { to: '/browse/report-image-ws' },
  // N17 PowerView (PB caption "&PowerView" in m_main, "&DashBoard" in the PHFC ancestor) →
  // w_dashboard, slice-284.
  m_dashboard: { to: '/reports/dashboard' },
  // N18 Web Report (PB "&Web Report") → w_web_report, slice-284; m_main OVERRIDES the PHFC
  // handler so the target really is w_web_report, not w_phfc_web_report.
  m_webreport: { to: '/reports/web-report' },
  // N62 Help > About (PB "&About" → w_phfc_about): product/company block + "what build am I
  // on?" (slice, commit, server start) + session scope. New page, slice-3454.
  m_about: { to: '/about' },

  // ── slice-3471: two more already-built screens, plus one alias ────────────────────
  // (Chris, 2026-08-25, ph10-nav-decisions.html rows N24/N25/N60 — "I agree with all
  // recommendations".) Same mechanism as the slice-3454 block above: key the ROUTE by menu
  // id, add no WINDOW_TO_ROUTE entry, so the tester catalog — which walks windows — is
  // untouched and no ph10_test_page row moves.
  //
  // N24 Configuration > System > Field Format > Field Format Rules → PB
  // w_phfc_field_format_rules, ported in slice-663 over dbxx.field_format_rules (18 rows),
  // full add/save/delete; TableBrowserRegistry.java registers the "field-format-rules" def.
  // The screen has shipped for months with no menu leaf reaching it.
  m_fieldformatrules: { to: '/browse/field-format-rules' },
  // N25 …> Field Formats → PB w_phfc_field_formats, ported in slice-660 over
  // dbxx.field_formats_header (8 rows), CRUD, drilling to the slice-649 detail page
  // /field-formats/:fieldFormat/details over field_formats_detail (17 rows). Standing
  // caveat, recorded so nobody rediscovers it: PH10 does not yet APPLY these input masks at
  // runtime, so this exposes maintenance for data nothing consumes yet. That is an argument
  // for the runtime follow-up, not for hiding two finished screens.
  m_fieldformats: { to: '/browse/field-formats' },
  // N60 Help > Tools > Serial Port Test → PB w_phfc_serial_port_test, a COM-port diagnostic
  // for scales/scanners cabled to the DESKTOP client. There is no browser equivalent of a
  // raw COM port, but PH10 already ships the browser-era answer to the same question:
  // /hardware-test (slice-1608) — scale reads + Zebra ZPL station diagnostics. ALIAS, not a
  // port: a PB user hunting for the port test where they know it lives finds the working
  // equivalent, and Help > Tools keeps a live leaf instead of emptying out (its only sibling,
  // PB Color Selector, went in slice-3454). Since slice-3590 (Chris trimmed the PH10 group)
  // this alias is the ONLY sidebar path to /hardware-test.
  m_serialporttest: { to: '/hardware-test' },

  // ── slice-3469: the four unbuilt w_pre_report reports ─────────────────────────────
  // (Chris, 2026-08-25, ph10-nav-decisions.html N4/N5/N6/N11.) Ordinary registry ports —
  // every DataWindow is in the PB export and every proc is live in chris_900; they were
  // missed by the reports campaign only because it walked the Reports menu and three of
  // these hang under Activities. Same one-line-per-leaf mechanism as the slice-288 block
  // above; the criteria form, SP dispatch and PDF all come from
  // docs/pb-reports-registry.json.
  //
  // N4/N5/N6 — Activities > Restocking (PB string1=pre_report_misc, uo_pre_report_misc).
  m_restockpicksbywave: { to: '/reports/pb/rstk-picks-by-wave' },
  m_restockpicksbylocation: { to: '/reports/pb/rstk-picks-by-loc' },
  m_restocksbypickruleandwave: { to: '/reports/pb/rstk-by-rule-and-wave' },
  // N11 — Reports > Shipments, alongside its two LIVE siblings m_shippedcartonsbystore
  // and m_shippedcartonsbyshipmethod (string1=pre_report_shipment).
  m_shippedcartonsbyshift: { to: '/reports/pb/ship-cartons-by-shift' },

  // ── slice-3470: Workstations maintenance ──────────────────────────────────────────
  // (Chris, 2026-08-25, ph10-nav-decisions.html N21.) PB m_workstations' clicked event
  // delegates to the hidden PHFC twin (m_frame.m_configuration.m_system_phfc
  // .m_workstations_phfc.event trigger clicked()), so the menu parse recorded no window
  // and the leaf resolved to nothing — the same shape as the eight slice-3454 wires.
  // Target is the generic browse def over dbxx.workstation, conformed to
  // dw_phfc_workstation this slice.
  m_workstations: { to: '/browse/workstation' },
};

/**
 * Display-label overrides keyed by PB menu id. Used when the operator-facing name
 * should differ from what PB shipped (typically because PB's name is too long or
 * uses different language than PH10's product positioning). Sidebar + NavSearch
 * call {@link resolveLabel} which checks this map first; everything else falls
 * back to {@code item.label} from {@code nav.generated.json}.
 */
/**
 * PB menu leaves that fire an APP ACTION instead of opening a screen — slice-3454
 * (nav-graybar review N55 + N58). Both are Help-menu items whose PB event pokes the running
 * app: Help Contents (F1) opens context help for the active sheet, Menu Navigator (F9) opens
 * PB's searchable menu tree. PH10 already has both surfaces (the per-screen help popover and
 * the sidebar search box), so the leaf dispatches the matching app-shortcut event.
 * {@code Sidebar} renders these as buttons; the accelerators are bound in the same
 * components that own the surface, so F1/F9 work app-wide as they do in PB.
 */
export const ID_TO_ACTION: Record<
  string,
  { event: AppShortcutEvent; shortcut: 'F1' | 'F9'; hint: string }
> = {
  m_index: {
    event: OPEN_SCREEN_HELP_EVENT,
    shortcut: 'F1',
    hint: 'Help for the screen you are on (F1)',
  },
  m_menumapping: {
    event: FOCUS_NAV_SEARCH_EVENT,
    shortcut: 'F9',
    hint: 'Search the whole menu (F9)',
  },
};

export const ID_TO_LABEL: Record<string, string> = {
  // slice-307: rename PB's "AI Query Report Assistant Tool" -> "Ask Me Anything
  // Reporting" -- matches the POC1 product name that operators already know.
  m_34: 'Ask Me Anything Reporting',
};

/** Display label for a nav item, honoring per-id overrides from {@link ID_TO_LABEL}. */
export function resolveLabel(item: NavTreeItem): string | null {
  if (item.id && ID_TO_LABEL[item.id]) return ID_TO_LABEL[item.id]!;
  return item.label;
}

/** Resolve a tree item to a route + ACL gate, or null if it's an unported leaf. */
export function resolveRoute(item: NavTreeItem): RouteEntry | null {
  // Per-id override wins (covers PB's "one window, two menu items, two modes" pattern).
  if (item.id && ID_TO_ROUTE[item.id]) return ID_TO_ROUTE[item.id]!;
  if (!item.window) return null;
  return WINDOW_TO_ROUTE[item.window] ?? null;
}

/**
 * ADR-0067 decision 6/9: the app's one window checker, bound to lane B's generated maps
 * (`generated/route-windows.json`, `generated/window-policies.json`). The sidebar and the
 * router's `RequireWindow` guard ask THIS — nothing reads the maps or `allowedWindows` directly.
 */
export const APP_SCREEN_ACCESS: ScreenAccess = createScreenAccess({
  routeWindows: routeWindowsData as WindowCatalog['routeWindows'],
  policies: windowPoliciesData as unknown as WindowCatalog['policies'],
});

/** How the sidebar renders an item: a live link, a greyed non-clickable row, or nothing. */
export type NavItemState = 'enabled' | 'disabled' | 'hidden';

/**
 * ADR-0067 decision 6: the sidebar verdict for one item.
 *
 * <p>LOG mode (tonight, and any session persisted by an older build): EXACTLY today's answer —
 * the PB-hidden / devDisabled drops plus the `requiredModule` level check — never `disabled`.
 *
 * <p>ENFORCE mode: for a route the window map knows, visibility is PRESENCE through the checker
 * (any of the route's windows in `allowedWindows`, or `menu-open`), not module level; a failing
 * item is greyed (`disabled`) or `hidden` per `crt_setting` Client/HideDisabledMenu (PB
 * `f_phfc_menu_security.srf:62-70`). Routes the map does not know keep the level check.
 */
export function navItemState(
  item: NavTreeItem,
  moduleLevels: Record<string, number>,
  session?: AuthSession | null,
  access: ScreenAccess = APP_SCREEN_ACCESS,
): NavItemState {
  if (isDroppedFromMenu(item)) return 'hidden';
  const presence = enforcedPresence(item, session, access);
  if (presence === null) return isItemVisibleByLevel(item, moduleLevels) ? 'enabled' : 'hidden';
  if (presence) return 'enabled';
  return session?.hideDisabledMenu ? 'hidden' : 'disabled';
}

/**
 * The presence answer when it governs the item — enforce mode, a session that carries
 * `allowedWindows`, and a route the window map knows — else null (the level rule governs).
 */
function enforcedPresence(
  item: NavTreeItem,
  session: AuthSession | null | undefined,
  access: ScreenAccess,
): boolean | null {
  if (!session || windowGateModeOf(session) !== 'enforce' || !hasLoadedGrants(session)) return null;
  return presenceVerdict(item, session, access);
}

/** true/false = the checker's presence answer; null = the route is not in the window map. */
function presenceVerdict(
  item: NavTreeItem,
  session: AuthSession,
  access: ScreenAccess,
): boolean | null {
  const route = resolveRoute(item);
  if (!route) return null;
  const pattern = access.matchRoutePattern(route.to);
  if (!pattern) return null;
  const v = access.mayOpenRoute(session, pattern);
  return v.reason === 'unmapped' ? null : v.allowed;
}

function isDroppedFromMenu(item: NavTreeItem): boolean {
  if (item.id && (HIDDEN_IN_PB_NAV_IDS.has(item.id) || TRIAGED_OUT_NAV_IDS.has(item.id))) {
    return true;
  }
  const route = resolveRoute(item);
  return !!(route?.hiddenInPb || route?.devDisabled);
}

/**
 * Log-mode disagreement summary (ADR-0067 decision 8): how many live menu leaves the module-level
 * rule and the window-presence rule would answer differently for this session. The sidebar
 * `console.debug`s it once per session; nothing is sent to the server (one line per item would
 * flood the sink).
 */
export function navPresenceDisagreements(
  tree: NavTreeItem[],
  moduleLevels: Record<string, number>,
  session: AuthSession,
  access: ScreenAccess = APP_SCREEN_ACCESS,
): { levelOnly: number; presenceOnly: number } {
  let levelOnly = 0;
  let presenceOnly = 0;
  if (!hasLoadedGrants(session)) return { levelOnly, presenceOnly };
  for (const { item } of livePoints(tree)) {
    if (isDroppedFromMenu(item)) continue;
    const presence = presenceVerdict(item, session, access);
    if (presence === null) continue;
    const level = isItemVisibleByLevel(item, moduleLevels);
    if (level && !presence) levelOnly++;
    else if (!level && presence) presenceOnly++;
  }
  return { levelOnly, presenceOnly };
}

/**
 * ACL filter for a single item (route gate + module level). With a `session` in enforce mode the
 * level check gives way to window presence ({@link navItemState}); a `disabled` item still counts
 * as visible here (it renders, greyed). Without a session — or in log mode — today's answer.
 */
export function isItemVisible(
  item: NavTreeItem,
  moduleLevels: Record<string, number>,
  session?: AuthSession | null,
  access: ScreenAccess = APP_SCREEN_ACCESS,
): boolean {
  return navItemState(item, moduleLevels, session, access) !== 'hidden';
}

/** Today's (pre-ADR-0067) sidebar rule: PB-hidden drops + `requiredModule` level. */
function isItemVisibleByLevel(
  item: NavTreeItem,
  moduleLevels: Record<string, number>,
): boolean {
  // slice-3454: the id-keyed check comes FIRST, before resolveRoute(). The route-borne
  // hiddenInPb flag below can only be reached through a route, so a PB-hidden item with NO
  // route (caption + clicked event inherited from m_phfc_main) slipped past the filter and
  // rendered as a dead dimmed row — and a hidden BRANCH could not be expressed at all.
  // NAV_TREE is already pruned of these ids; this keeps any consumer that builds its own
  // item objects (tests, generators, favorites replay) honest too.
  if (item.id && (HIDDEN_IN_PB_NAV_IDS.has(item.id) || TRIAGED_OUT_NAV_IDS.has(item.id))) {
    return false;
  }
  const route = resolveRoute(item);
  // PB parity (Chris 2026-07-13): PB creates these menu items visible=false and never
  // un-hides them — the screens are unreachable in the running PB app. Mirror that.
  if (route?.hiddenInPb) return false;
  // devDisabled: exists in PB but pulled from PH10 for now (Bob 2026-07-16). Same drop
  // behaviour as hiddenInPb; separate flag so the reason stays honest (PB is NOT hiding it).
  if (route?.devDisabled) return false;
  if (!route?.requiredModule) return true; // unported leaves stay visible (grayed)
  const level = moduleLevels[route.requiredModule] ?? 0;
  return level >= (route.requiredLevel ?? 1);
}

/** ACL filter for an extra item (top-level shortcut). */
export function isExtraVisible(
  item: ExtraNavItem,
  moduleLevels: Record<string, number>,
): boolean {
  if (!item.requiredModule) return true;
  const level = moduleLevels[item.requiredModule] ?? 0;
  return level >= (item.requiredLevel ?? 1);
}

/** Walk the tree and yield every leaf with a live route — used by the favorites surface. */
export function* livePoints(tree: NavTreeItem[]): Generator<{
  item: NavTreeItem;
  route: RouteEntry;
}> {
  for (const it of tree) {
    const r = resolveRoute(it);
    if (r) yield { item: it, route: r };
    if (it.children) yield* livePoints(it.children);
  }
}

/** Look up an item by route — LAST leaf wins where two PB menu items share one route, so
 *  this cannot identify WHICH leaf the operator meant. Favorites moved off it in slice-3641
 *  (see {@link findById}); kept for any consumer that genuinely wants "a leaf for this URL". */
let _routeIndex: Map<string, { item: NavTreeItem; route: RouteEntry }> | null = null;
export function findByRoute(to: string): { item: NavTreeItem; route: RouteEntry } | null {
  if (!_routeIndex) {
    _routeIndex = new Map();
    for (const point of livePoints(NAV_TREE)) _routeIndex.set(point.route.to, point);
  }
  return _routeIndex.get(to) ?? null;
}

/**
 * slice-3641 — id-keyed leaf lookup, the index Favorites resolves against.
 *
 * <p>Two bugs forced this. (1) {@link findByRoute} indexes {@link NAV_TREE} ONLY, so the
 * demo-instance leaves that {@link withDemoNav} injects (RF Simulator, Warehouse Status, the
 * Reports &gt; Labor Slingshot items, Work visualization) resolved to nothing — starring one
 * wrote the pref and lit the star, but the Favorites section silently dropped the row.
 * (2) Six pairs of PB menu leaves SHARE one route (Tag Order Search / Direct-Bulk Tag Order
 * Search, New Truck / Truck Search, Lost-Found Search / (Admin), the two Manual Location
 * Review leaves, the two Manual Item Review leaves, the two Label Mapping leaves), and a
 * route-keyed Map can only hold one of them — so starring one displayed the other's caption.
 * The PB menu id is unique per leaf ({@code navFavoritesIdKeying.test.ts} pins that), so it is the
 * only correct favourites key.
 *
 * <p>Callers pass the tree they are RENDERING (the demo-aware one from {@link withDemoNav}),
 * so a demo leaf resolves when the flag is on and is quietly invisible when it is off — the
 * stored id is never deleted, just not rendered on that instance.
 */
const _idIndexCache = new WeakMap<
  readonly NavTreeItem[],
  Map<string, { item: NavTreeItem; route: RouteEntry }>
>();

/** Map every live leaf in {@code tree} by its PB menu id. FIRST leaf wins on a duplicate id
 *  (there are none today — {@code navFavoritesIdKeying.test.ts} pins that). */
export function buildIdIndex(
  tree: readonly NavTreeItem[],
): Map<string, { item: NavTreeItem; route: RouteEntry }> {
  const cached = _idIndexCache.get(tree);
  if (cached) return cached;
  const out = new Map<string, { item: NavTreeItem; route: RouteEntry }>();
  for (const point of livePoints(tree as NavTreeItem[])) {
    if (point.item.id && !out.has(point.item.id)) out.set(point.item.id, point);
  }
  _idIndexCache.set(tree, out);
  return out;
}

/** Look up a live leaf by its PB menu id. Defaults to the static PB tree; pass the
 *  demo-aware tree to see the demo-only leaves. */
export function findById(
  id: string,
  tree: readonly NavTreeItem[] = NAV_TREE,
): { item: NavTreeItem; route: RouteEntry } | null {
  return buildIdIndex(tree).get(id) ?? null;
}

/** The id of the FIRST leaf (tree order) whose resolved route is {@code to}, or null when no
 *  leaf owns that route. Used once, to migrate route-keyed favourites written before
 *  slice-3641 — on a shared route the operator's intent is unrecoverable, so first-in-menu
 *  order is the tie-break (it is also what the sidebar reads top-down). */
export function findFirstIdForRoute(
  to: string,
  tree: readonly NavTreeItem[] = NAV_TREE,
): string | null {
  for (const point of livePoints(tree as NavTreeItem[])) {
    if (point.route.to === to && point.item.id) return point.item.id;
  }
  return null;
}

/** Built lazily alongside {@link _routeIndex}: maps a {@code route.to} to the ancestor IDs
 *  (group → intermediate branch → …) leading to that leaf. The leaf's own id is NOT included
 *  -- callers (Sidebar) use this to expand the branches above the active route. */
let _ancestorIndex: Map<string, string[]> | null = null;
function buildAncestorIndex(): Map<string, string[]> {
  const out = new Map<string, string[]>();
  function walk(items: NavTreeItem[], path: string[]) {
    for (const it of items) {
      const here = it.id ? [...path, it.id] : path;
      const r = resolveRoute(it);
      if (r) {
        // Ancestor chain excludes the leaf's own id -- we want to expand the branches
        // ABOVE the active route, not toggle the leaf itself.
        const ancestors = it.id ? here.slice(0, -1) : here;
        out.set(r.to, ancestors);
      }
      if (it.children?.length) walk(it.children, here);
    }
  }
  walk(NAV_TREE, []);
  return out;
}

/** Return the ancestor-id chain leading to the menu leaf whose route matches {@code to}, or
 *  {@code null} when nothing in the nav tree matches. Used by the sidebar to auto-expand the
 *  trail when the operator deep-links into a screen.
 *
 *  <p>Path matching first tries an exact route lookup; if that misses (the URL has params or
 *  trailing segments not in the nav tree -- e.g. {@code /orders/maintenance/12345} when the
 *  tree only knows {@code /orders/maintenance}), we walk back up path segments until either a
 *  parent route matches or we hit the root. Returns the longest matching chain. */
export function findAncestorIdsForRoute(to: string): string[] | null {
  if (!_ancestorIndex) _ancestorIndex = buildAncestorIndex();
  // Strip query string + hash; React Router gives us pathname so this is belt-and-suspenders.
  const clean = to.split('?')[0]?.split('#')[0] ?? '';
  if (!clean) return null;
  const direct = _ancestorIndex.get(clean);
  if (direct) return direct;
  // Walk up path segments: /orders/maintenance/12345 -> /orders/maintenance -> /orders.
  let trimmed = clean;
  while (trimmed.includes('/')) {
    trimmed = trimmed.substring(0, trimmed.lastIndexOf('/'));
    if (!trimmed) break;
    const hit = _ancestorIndex.get(trimmed);
    if (hit) return hit;
  }
  return null;
}

/** First-run default expansion. Was historically eager (every group containing a live leaf
 *  open on first load), which gave brand-new users a usable but very noisy sidebar -- by
 *  slice-318 we had ~400 LIVE screens and a handful of users' worth of PB top-menu groups all
 *  open at once. Now empty: the sidebar starts fully collapsed and the active-route effect in
 *  {@code Sidebar.tsx} auto-expands just the trail to wherever the operator is. */
export const DEFAULT_EXPANDED: Record<string, boolean> = {};

/**
 * slice-3626 (ADR-0050): the demo instance's extra menu leaves (Chris 2026-09-11,
 * docs/bringlaborover.txt): the seven Slingshot labor reports under Reports > Labor, and an
 * "RF Simulator" leaf at the bottom of Activities. Pure function of the PB tree so the PB
 * projection itself (nav.generated.json) stays untouched; returns the input unchanged when the
 * flag is off, so non-demo instances render exactly the PB menu.
 */
export const DEMO_LABOR_REPORT_ITEMS: NavTreeItem[] = [
  { id: 'slingshot_floor_pulse', label: 'Floor Pulse (Live)', window: 'slingshot_floor_pulse' },
  { id: 'slingshot_labor_by_user', label: 'Labor Productivity by User', window: 'slingshot_labor_by_user' },
  { id: 'slingshot_labor_uph', label: 'Labor UPH by Department', window: 'slingshot_labor_uph' },
  { id: 'slingshot_monthly_kpi', label: 'Monthly KPI Report', window: 'slingshot_monthly_kpi' },
  { id: 'slingshot_shift_recap', label: 'Shift Recap', window: 'slingshot_shift_recap' },
  { id: 'slingshot_user_scorecard', label: 'User Scorecard', window: 'slingshot_user_scorecard' },
  { id: 'slingshot_user_travel', label: 'User Travel', window: 'slingshot_user_travel' },
  // slice-3627 (Chris): the POC's Labor Task Assignment workbench, filed with the reports.
  { id: 'slingshot_labor_task_assign', label: 'Labor Task Assignment', window: 'slingshot_labor_task_assign' },
  // slice-3634 (Chris 2026-09-12, docs/workvisuals.txt): "reports/labor section … call it 'Work visualization'".
  { id: 'slingshot_work_visualization', label: 'Work visualization', window: 'slingshot_work_visualization' },
];

export const DEMO_RF_SIMULATOR_ITEM: NavTreeItem = {
  id: 'slingshot_rf_simulator',
  label: 'RF Simulator',
  window: 'slingshot_rf_simulator',
};

/**
 * slice-3639 (docs/design/warehouse-status.md): the KPI / attention board. Chris 2026-09-13:
 * "make this live under Manage Warehouse — no submenu — call it 'Warehouse Status'" — hence a
 * direct leaf FIRST in {@code m_managewarehouse}, not a group with one child.
 */
export const DEMO_WAREHOUSE_STATUS_ITEM: NavTreeItem = {
  id: 'slingshot_warehouse_status',
  label: 'Warehouse Status',
  window: 'slingshot_warehouse_status',
};

/**
 * slice-4482 (John 29 Oct): {@code rfSimEnabled} adds only Activities > RF Simulator — a non-demo
 * box (ph10-test) that turns on {@code ph10.rf-simulator.enabled} gets the simulator without the
 * rest of the demo leaves. Defaults to {@code demoEnabled}, so a demo box always has it.
 */
export function withDemoNav(
  tree: NavTreeGroup[],
  demoEnabled: boolean,
  rfSimEnabled: boolean = demoEnabled,
): NavTreeGroup[] {
  if (!demoEnabled && !rfSimEnabled) return tree;
  if (!demoEnabled) {
    return tree.map((group) =>
      group.id === 'm_activities'
        ? { ...group, children: [...group.children, DEMO_RF_SIMULATOR_ITEM] }
        : group,
    );
  }
  return tree.map((group) => {
    if (group.id === 'm_managewarehouse') {
      return { ...group, children: [DEMO_WAREHOUSE_STATUS_ITEM, ...group.children] };
    }
    if (group.id === 'm_reports') {
      let placed = false;
      const children = group.children.map((c) => {
        if (c.id === 'm_labor') {
          placed = true;
          return { ...c, children: [...(c.children ?? []), ...DEMO_LABOR_REPORT_ITEMS] };
        }
        return c;
      });
      if (!placed) {
        children.push({ id: 'm_labor', label: 'Labor', children: [...DEMO_LABOR_REPORT_ITEMS] });
      }
      return { ...group, children };
    }
    if (group.id === 'm_activities' && rfSimEnabled) {
      return { ...group, children: [...group.children, DEMO_RF_SIMULATOR_ITEM] };
    }
    return group;
  });
}

/**
 * slice-3641: the tree with EVERY demo leaf present, regardless of the instance flag. Used
 * only to migrate route-keyed favourites to menu ids (see {@link findFirstIdForRoute}) —
 * resolving the legacy list against the full tree means a demo screen's star survives on an
 * instance where the flag happens to be off instead of being dropped on first read. Rendering
 * still goes through the flag-aware tree, so nothing demo-only shows up where it shouldn't.
 */
export const NAV_TREE_WITH_DEMO: NavTreeGroup[] = withDemoNav(NAV_TREE, true);
